import React, {
  useContext,
  useEffect,
  useState
} from "react";

import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  TextField,
  Typography
} from "@material-ui/core";

import MailOutlineIcon from "@material-ui/icons/MailOutline";
import LockOutlinedIcon from "@material-ui/icons/LockOutlined";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import VpnKeyOutlinedIcon from "@material-ui/icons/VpnKeyOutlined";
import CloseIcon from "@material-ui/icons/Close";
import RefreshIcon from "@material-ui/icons/Refresh";
import SearchIcon from "@material-ui/icons/Search";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import InboxIcon from "@material-ui/icons/Inbox";
import ReplyIcon from "@material-ui/icons/Reply";
import SendIcon from "@material-ui/icons/Send";
import ForwardIcon from "@material-ui/icons/Forward";
import CreateIcon from "@material-ui/icons/Create";
import GetAppIcon from "@material-ui/icons/GetApp";

import Autocomplete from "@material-ui/lab/Autocomplete";

import { useHistory } from "react-router-dom";

import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import EmailAccessManager from "./EmailAccessManager";
import EmailSignatureManager from "./EmailSignatureManager";
import RichEmailEditor from "./RichEmailEditor";
import EmailHtmlViewer from "./EmailHtmlViewer";
import EmailWorkspaceV2 from "./EmailWorkspaceV2";


const safeArray = value =>
  Array.isArray(value)
    ? value
    : [];



// KORAX EMAIL DRAFTS V1

const EMAIL_DRAFT_DB =
  "korax-email-drafts-v1";

const EMAIL_DRAFT_STORE =
  "drafts";


const openEmailDraftDb =
  () =>
    new Promise(
      (
        resolve,
        reject
      ) => {

        if (
          !window.indexedDB
        ) {

          reject(
            new Error(
              "IndexedDB indisponível."
            )
          );

          return;
        }


        const request =
          window.indexedDB.open(
            EMAIL_DRAFT_DB,
            1
          );


        request.onupgradeneeded =
          event => {

            const db =
              event.target.result;


            if (
              !db.objectStoreNames
                .contains(
                  EMAIL_DRAFT_STORE
                )
            ) {

              db.createObjectStore(
                EMAIL_DRAFT_STORE,
                {
                  keyPath:
                    "key"
                }
              );
            }
          };


        request.onsuccess =
          () =>
            resolve(
              request.result
            );


        request.onerror =
          () =>
            reject(
              request.error
            );
      }
    );


const getEmailDraft =
  async key => {

    if (!key) {
      return null;
    }


    const db =
      await openEmailDraftDb();


    return new Promise(
      (
        resolve,
        reject
      ) => {

        const transaction =
          db.transaction(
            EMAIL_DRAFT_STORE,
            "readonly"
          );


        const store =
          transaction.objectStore(
            EMAIL_DRAFT_STORE
          );


        const request =
          store.get(
            key
          );


        request.onsuccess =
          () => {

            resolve(
              request.result ||
              null
            );
          };


        request.onerror =
          () =>
            reject(
              request.error
            );


        transaction.oncomplete =
          () =>
            db.close();


        transaction.onerror =
          () =>
            db.close();
      }
    );
  };


const saveEmailDraft =
  async draft => {

    if (
      !draft?.key
    ) {
      return;
    }


    const db =
      await openEmailDraftDb();


    return new Promise(
      (
        resolve,
        reject
      ) => {

        const transaction =
          db.transaction(
            EMAIL_DRAFT_STORE,
            "readwrite"
          );


        transaction
          .objectStore(
            EMAIL_DRAFT_STORE
          )
          .put(
            draft
          );


        transaction.oncomplete =
          () => {

            db.close();

            resolve();
          };


        transaction.onerror =
          () => {

            db.close();

            reject(
              transaction.error
            );
          };
      }
    );
  };


const deleteEmailDraft =
  async key => {

    if (!key) {
      return;
    }


    const db =
      await openEmailDraftDb();


    return new Promise(
      (
        resolve,
        reject
      ) => {

        const transaction =
          db.transaction(
            EMAIL_DRAFT_STORE,
            "readwrite"
          );


        transaction
          .objectStore(
            EMAIL_DRAFT_STORE
          )
          .delete(
            key
          );


        transaction.oncomplete =
          () => {

            db.close();

            resolve();
          };


        transaction.onerror =
          () => {

            db.close();

            reject(
              transaction.error
            );
          };
      }
    );
  };


// KORAX EMAIL MESSAGE FLAGS V1

const EMAIL_FLAG_SEEN =
  "\\Seen";

const EMAIL_FLAG_FAVORITE =
  "\\Flagged";


const emailHasFlag = (
  message,
  flag
) =>
  safeArray(
    message?.flags
  ).some(
    item =>
      String(item) ===
      String(flag)
  );


const sameEmailMessage = (
  first,
  second
) => {

  if (
    !first ||
    !second
  ) {
    return false;
  }


  return (
    Number(
      first?.mailboxId ||
      first?.mailbox?.id
    ) ===
      Number(
        second?.mailboxId ||
        second?.mailbox?.id
      ) &&
    Number(
      first?.uid
    ) ===
      Number(
        second?.uid
      )
  );
};


const formatDate = value => {

  if (!value) {
    return "";
  }

  const date =
    new Date(value);

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return "";
  }

  const now =
    new Date();

  const sameDay =
    date.getFullYear() ===
      now.getFullYear() &&
    date.getMonth() ===
      now.getMonth() &&
    date.getDate() ===
      now.getDate();

  if (sameDay) {

    return date.toLocaleTimeString(
      "pt-BR",
      {
        hour:
          "2-digit",

        minute:
          "2-digit"
      }
    );
  }

  return date.toLocaleDateString(
    "pt-BR",
    {
      day:
        "2-digit",

      month:
        "2-digit"
    }
  );
};


const senderLabel = message => {

  const name =
    String(
      message?.from?.name ||
      ""
    ).trim();

  const address =
    String(
      message?.from?.address ||
      ""
    ).trim();

  return (
    name ||
    address ||
    "Remetente"
  );
};


const formatAddressList = value => {

  const rows =
    Array.isArray(value)
      ? value
      : [];

  return rows
    .map(
      item => {

        const name =
          String(
            item?.name ||
            ""
          ).trim();

        const address =
          String(
            item?.address ||
            ""
          ).trim();

        if (
          name &&
          address
        ) {
          return `${name} <${address}>`;
        }

        return (
          address ||
          name
        );
      }
    )
    .filter(Boolean)
    .join(", ");
};


const formatFullDate = value => {

  if (!value) {
    return "";
  }

  const date =
    new Date(value);

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return "";
  }

  return date.toLocaleString(
    "pt-BR",
    {
      day:
        "2-digit",

      month:
        "2-digit",

      year:
        "numeric",

      hour:
        "2-digit",

      minute:
        "2-digit"
    }
  );
};


const parseEmailList = value =>
  String(value || "")
    .split(/[;,]+/)
    .map(
      item =>
        item.trim()
    )
    .filter(Boolean);


const fileToBase64 = file =>
  new Promise(
    (
      resolve,
      reject
    ) => {

      const reader =
        new FileReader();

      reader.onload = () => {

        const result =
          String(
            reader.result || ""
          );

        const comma =
          result.indexOf(",");

        resolve(
          comma >= 0
            ? result.slice(
                comma + 1
              )
            : result
        );
      };

      reader.onerror = () => {

        reject(
          new Error(
            "Não foi possível ler o arquivo."
          )
        );
      };

      reader.readAsDataURL(
        file
      );
    }
  );


const bytesLabel = value => {

  const bytes =
    Number(value || 0);

  if (
    bytes <
    1024
  ) {
    return `${bytes} B`;
  }

  if (
    bytes <
    1024 * 1024
  ) {
    return `${(
      bytes / 1024
    ).toFixed(1)} KB`;
  }

  return `${(
    bytes /
    1024 /
    1024
  ).toFixed(1)} MB`;
};


// KORAX EMAIL FOLDER MAP V1

const EMAIL_FOLDER_PATHS = {
  inbox: "INBOX",
  sent: "INBOX.Sent",
  archive: "INBOX.Archive",
  spam: "INBOX.Junk",
  trash: "INBOX.Trash"
};

const EMAIL_FOLDER_ENDPOINTS = {
  inbox: "/email-beta/inbox",
  sent: "/email-beta/sent",
  archive: "/email-beta/archive",
  spam: "/email-beta/spam",
  trash: "/email-beta/trash"
};


const EmailBeta = () => {

  const history =
    useHistory();

  const { user } =
    useContext(
      AuthContext
    );

  const [
    loading,
    setLoading
  ] = useState(true);

  const [
    companies,
    setCompanies
  ] = useState([]);

  const [
    selectedCompanyId,
    setSelectedCompanyId
  ] = useState("");

  const [
    providerStatus,
    setProviderStatus
  ] = useState(null);

  const [
    inbox,
    setInbox
  ] = useState(null);

  const [
    inboxLoading,
    setInboxLoading
  ] = useState(false);

  const [
    search,
    setSearch
  ] = useState("");

  const [
    mailboxFilter,
    setMailboxFilter
  ] = useState("all");

  const [
    unreadOnly,
    setUnreadOnly
  ] = useState(false);

  // KORAX EMAIL FILTERS V1.2
  const [
    attachmentOnly,
    setAttachmentOnly
  ] = useState(false);

  const [
    dateFilter,
    setDateFilter
  ] = useState("all");


  const [
    mailFolder,
    setMailFolder
  ] = useState("inbox");

  const [
    downloadingAttachmentId,
    setDownloadingAttachmentId
  ] = useState("");


  const [
    selectedMessage,
    setSelectedMessage
  ] = useState(null);

  const [
    selectedMessageSummary,
    setSelectedMessageSummary
  ] = useState(null);

  const [
    messageLoading,
    setMessageLoading
  ] = useState(false);


  const [
    replyMode,
    setReplyMode
  ] = useState(false);

  const [
    replyText,
    setReplyText
  ] = useState("");

  const [
    replyHtml,
    setReplyHtml
  ] = useState("");

  const [
    replyInlineImages,
    setReplyInlineImages
  ] = useState([]);



  const [
    replyAttachments,
    setReplyAttachments
  ] = useState([]);

  const [
    replyAttachmentError,
    setReplyAttachmentError
  ] = useState("");

  const [
    replyAttachmentLoading,
    setReplyAttachmentLoading
  ] = useState(false);

  const [
    replyConfirming,
    setReplyConfirming
  ] = useState(false);

  const [
    replySending,
    setReplySending
  ] = useState(false);

  const [
    replySent,
    setReplySent
  ] = useState(false);


  const [
    composeOpen,
    setComposeOpen
  ] = useState(false);

  const [
    composeMode,
    setComposeMode
  ] = useState("new");

  const [
    composeMailboxId,
    setComposeMailboxId
  ] = useState("");

  const [
    composeTo,
    setComposeTo
  ] = useState("");

  const [
    recipientSuggestions,
    setRecipientSuggestions
  ] = useState([]);

  const [
    composeCc,
    setComposeCc
  ] = useState("");

  const [
    composeBcc,
    setComposeBcc
  ] = useState("");

  const [
    composeSubject,
    setComposeSubject
  ] = useState("");

  const [
    composeText,
    setComposeText
  ] = useState("");

  // KORAX RICH EDITOR V4
  const [
    composeHtml,
    setComposeHtml
  ] = useState("");

  // KORAX INLINE BODY IMAGES V2
  const [
    composeInlineImages,
    setComposeInlineImages
  ] = useState([]);



  const [
    composeAttachments,
    setComposeAttachments
  ] = useState([]);

  const [
    composeConfirming,
    setComposeConfirming
  ] = useState(false);

  const [
    composeSending,
    setComposeSending
  ] = useState(false);

  const [
    composeSuccess,
    setComposeSuccess
  ] = useState(null);

  const [
    composeError,
    setComposeError
  ] = useState("");


  const [
    composeDraftKey,
    setComposeDraftKey
  ] = useState("");

  const [
    composeDraftReady,
    setComposeDraftReady
  ] = useState(false);

  const [
    composeDraftStatus,
    setComposeDraftStatus
  ] = useState("");


  const [
    replyDraftKey,
    setReplyDraftKey
  ] = useState("");

  const [
    replyDraftReady,
    setReplyDraftReady
  ] = useState(false);

  const [
    replyDraftStatus,
    setReplyDraftStatus
  ] = useState("");


  /*
   * Modal Hostinger
   */

  const [
    providerModalOpen,
    setProviderModalOpen
  ] = useState(false);

  const [
    modalCompanyId,
    setModalCompanyId
  ] = useState("");

  const [
    apiKey,
    setApiKey
  ] = useState("");

  const [
    showApiKey,
    setShowApiKey
  ] = useState(false);

  const [
    testingConnection,
    setTestingConnection
  ] = useState(false);

  const [
    testResult,
    setTestResult
  ] = useState(null);

  const [
    selectedMailboxIds,
    setSelectedMailboxIds
  ] = useState([]);

  const [
    savingConnection,
    setSavingConnection
  ] = useState(false);

  const [
    saveResult,
    setSaveResult
  ] = useState(null);


  const loadCompanyData =
    async (
      companyId,
      folder = mailFolder
    ) => {

      const id =
        Number(companyId);

      if (
        !Number.isInteger(id) ||
        id <= 0
      ) {
        return;
      }

      setInboxLoading(true);

      try {

        const {
          data:
            statusData
        } =
          await api.get(
            "/email-beta/provider/status",
            {
              params: {
                companyId:
                  id
              }
            }
          );

        setProviderStatus(
          statusData
        );

        if (
          statusData?.connected
        ) {

          const endpoint =
            EMAIL_FOLDER_ENDPOINTS[folder] || "/email-beta/inbox";

          const {
            data:
              inboxData
          } =
            await api.get(
              endpoint,
              {
                params: {
                  companyId:
                    id,

                  limit:
                    50
                }
              }
            );

          setInbox(
            inboxData
          );

        } else {

          setInbox(null);
        }

      } catch (err) {

        setInbox(null);

        toastError(err);

      } finally {

        setInboxLoading(false);
      }
    };


  useEffect(() => {

    if (!user?.id) {
      return;
    }

    let active = true;

    const bootstrap =
      async () => {

        setLoading(true);

        try {

          const {
            data
          } =
            await api.get(
              "/email-beta/companies"
            );

          const rows =
            safeArray(
              data?.companies
            );

          if (!active) {
            return;
          }

          setCompanies(
            rows
          );

          if (!rows.length) {

            setLoading(false);

            return;
          }

          /*
           * Primeiro tentamos recuperar
           * apenas o ID da última empresa.
           *
           * Nunca salvamos token no browser.
           */
          const remembered =
            String(
              window.localStorage
                .getItem(
                  "koraxEmailCompanyId"
                ) ||
              ""
            );

          const rememberedExists =
            rows.some(
              company =>
                String(
                  company.id
                ) ===
                remembered
            );

          let initialId =
            rememberedExists
              ? remembered
              : "";

          /*
           * Primeira visita:
           * procura uma empresa já conectada.
           *
           * provider/status consulta apenas
           * nosso banco; não chama Hostinger.
           */
          if (!initialId) {

            for (
              const company
              of rows
            ) {

              try {

                const {
                  data:
                    companyStatus
                } =
                  await api.get(
                    "/email-beta/provider/status",
                    {
                      params: {
                        companyId:
                          company.id
                      }
                    }
                  );

                if (
                  companyStatus
                    ?.connected
                ) {

                  initialId =
                    String(
                      company.id
                    );

                  break;
                }

              } catch (_) {
                /*
                 * Continua buscando outra.
                 */
              }
            }
          }

          if (!initialId) {

            const currentCompany =
              rows.find(
                company =>
                  Number(
                    company.id
                  ) ===
                  Number(
                    user.companyId
                  )
              );

            initialId =
              String(
                currentCompany
                  ?.id ||
                rows[0].id
              );
          }

          if (!active) {
            return;
          }

          setSelectedCompanyId(
            initialId
          );

          window.localStorage
            .setItem(
              "koraxEmailCompanyId",
              initialId
            );

        } catch (err) {

          toastError(err);

        } finally {

          if (active) {

            setLoading(false);
          }
        }
      };

    bootstrap();

    return () => {

      active = false;
    };

  }, [
    user?.id,
    user?.super,
    user?.companyId,
    history
  ]);


  useEffect(() => {

    if (
      !selectedCompanyId
    ) {
      return;
    }

    setSearch("");
    setMailboxFilter("all");
    setUnreadOnly(false);
    setAttachmentOnly(false);
    setDateFilter("all");

    loadCompanyData(
      selectedCompanyId
    );

    // eslint-disable-next-line
  }, [
    selectedCompanyId,
    user?.super,
    mailFolder
  ]);


  const handleCompanyChange =
    event => {

      const value =
        String(
          event.target.value ||
          ""
        );

      setSelectedCompanyId(
        value
      );

      setSelectedMessage(null);
      setSelectedMessageSummary(null);

      setMailFolder(
        "inbox"
      );

      if (value) {

        window.localStorage
          .setItem(
            "koraxEmailCompanyId",
            value
          );
      }
    };


  const refreshInbox =
    async () => {

      if (
        !selectedCompanyId
      ) {
        return;
      }

      await loadCompanyData(
        selectedCompanyId
      );
    };


  /*
   * Hostinger modal
   */

  const openProviderModal = () => {

    setModalCompanyId(
      selectedCompanyId ||
      ""
    );

    setApiKey("");
    setShowApiKey(false);
    setTestResult(null);
    setSelectedMailboxIds([]);
    setSaveResult(null);

    setProviderModalOpen(
      true
    );
  };


  const closeProviderModal =
    async () => {

      if (
        testingConnection ||
        savingConnection
      ) {
        return;
      }

      setProviderModalOpen(
        false
      );

      setApiKey("");
      setShowApiKey(false);
      setTestResult(null);
      setSelectedMailboxIds([]);
      setSaveResult(null);

      if (
        selectedCompanyId
      ) {

        await loadCompanyData(
          selectedCompanyId
        );
      }
    };


  const handleApiKeyChange =
    event => {

      setApiKey(
        event.target.value
      );

      if (testResult) {

        setTestResult(null);

        setSelectedMailboxIds(
          []
        );

        setSaveResult(null);
      }
    };


  const handleTestConnection =
    async () => {

      const cleanToken =
        String(
          apiKey || ""
        ).trim();

      if (!cleanToken) {
        return;
      }

      setTestingConnection(
        true
      );

      setTestResult(null);
      setSelectedMailboxIds([]);
      setSaveResult(null);

      try {

        const {
          data
        } =
          await api.post(
            "/email-beta/provider/test",
            {
              provider:
                "hostinger",

              token:
                cleanToken
            }
          );

        setTestResult(
          data
        );

        const foundMailboxes =
          safeArray(
            data?.mailboxes
          );

        setSelectedMailboxIds(
          foundMailboxes
            .map(
              mailbox =>
                String(
                  mailbox
                    ?.resourceId ||
                  ""
                ).trim()
            )
            .filter(Boolean)
        );

      } catch (err) {

        toastError(err);

      } finally {

        setTestingConnection(
          false
        );
      }
    };


  const handleToggleMailbox =
    mailboxId => {

      const id =
        String(
          mailboxId || ""
        ).trim();

      if (!id) {
        return;
      }

      setSelectedMailboxIds(
        current =>
          current.includes(id)
            ? current.filter(
                value =>
                  value !== id
              )
            : [
                ...current,
                id
              ]
      );

      setSaveResult(null);
    };


  const testedMailboxes =
    safeArray(
      testResult?.mailboxes
    );


  const allMailboxesSelected =
    testedMailboxes.length >
      0 &&
    selectedMailboxIds.length ===
      testedMailboxes.length;


  const handleToggleAllMailboxes =
    () => {

      if (
        allMailboxesSelected
      ) {

        setSelectedMailboxIds(
          []
        );

        return;
      }

      setSelectedMailboxIds(
        testedMailboxes
          .map(
            mailbox =>
              String(
                mailbox
                  ?.resourceId ||
                ""
              ).trim()
          )
          .filter(Boolean)
      );
    };


  const handleSaveConnection =
    async () => {

      const cleanToken =
        String(
          apiKey || ""
        ).trim();

      const companyId =
        Number(
          modalCompanyId
        );

      if (
        !cleanToken ||
        !Number.isInteger(
          companyId
        ) ||
        companyId <= 0 ||
        selectedMailboxIds
          .length === 0
      ) {
        return;
      }

      setSavingConnection(
        true
      );

      setSaveResult(null);

      try {

        const {
          data
        } =
          await api.post(
            "/email-beta/provider/connect",
            {
              companyId,

              provider:
                "hostinger",

              token:
                cleanToken,

              selectedMailboxIds
            }
          );

        setSaveResult(
          data
        );

        const connectedCompanyId =
          String(
            data?.company?.id ||
            companyId
          );

        window.localStorage
          .setItem(
            "koraxEmailCompanyId",
            connectedCompanyId
          );

        setSelectedCompanyId(
          connectedCompanyId
        );

        setProviderStatus(
          data
        );

      } catch (err) {

        toastError(err);

      } finally {

        setSavingConnection(
          false
        );
      }
    };


  const applyEmailFlagsLocal =
    (
      message,
      addFlags = [],
      removeFlags = []
    ) => {

      const addSet =
        new Set(
          safeArray(
            addFlags
          ).map(String)
        );


      const removeSet =
        new Set(
          safeArray(
            removeFlags
          ).map(String)
        );


      const transform =
        row => {

          const flags =
            safeArray(
              row?.flags
            )
              .map(String)
              .filter(
                flag =>
                  !removeSet
                    .has(flag)
              );


          addSet.forEach(
            flag => {

              if (
                !flags.includes(
                  flag
                )
              ) {
                flags.push(
                  flag
                );
              }
            }
          );


          return {
            ...row,

            flags,

            unseen:
              !flags.includes(
                EMAIL_FLAG_SEEN
              )
          };
        };


      setInbox(
        current => {

          if (!current) {
            return current;
          }


          const rows =
            safeArray(
              current.messages
            );


          const index =
            rows.findIndex(
              row =>
                sameEmailMessage(
                  row,
                  message
                )
            );


          if (index < 0) {
            return current;
          }


          const oldRow =
            rows[index];

          const newRow =
            transform(
              oldRow
            );


          const nextRows =
            [...rows];

          nextRows[index] =
            newRow;


          const unreadDelta =
            (
              newRow.unseen
                ? 1
                : 0
            ) -
            (
              oldRow.unseen
                ? 1
                : 0
            );


          return {
            ...current,

            unreadCount:
              Math.max(
                0,
                Number(
                  current
                    .unreadCount ||
                  0
                ) +
                unreadDelta
              ),

            messages:
              nextRows
          };
        }
      );


      setSelectedMessageSummary(
        current => {

          if (
            !current ||
            !sameEmailMessage(
              current,
              message
            )
          ) {
            return current;
          }


          return transform(
            current
          );
        }
      );
    };


  const handleUpdateEmailFlags =
    async (
      message,
      {
        addFlags = [],
        removeFlags = []
      }
    ) => {

      const companyId =
        Number(
          selectedCompanyId
        );


      const mailboxId =
        Number(
          message?.mailboxId ||
          message?.mailbox?.id
        );


      const uid =
        Number(
          message?.uid
        );


      if (
        !Number.isInteger(
          companyId
        ) ||
        companyId <= 0 ||
        !Number.isInteger(
          mailboxId
        ) ||
        mailboxId <= 0 ||
        !Number.isInteger(uid) ||
        uid <= 0
      ) {
        return false;
      }


      try {

        await api.patch(
          "/email-beta/message/flags",
          {
            companyId,
            mailboxId,
            uid,

            folder:
              message?._folder ||
              EMAIL_FOLDER_PATHS[
                mailFolder
              ] ||
              "INBOX",

            addFlags,

            removeFlags
          }
        );


        applyEmailFlagsLocal(
          {
            ...message,
            mailboxId,
            uid
          },
          addFlags,
          removeFlags
        );

        if (
          addFlags.includes(EMAIL_FLAG_SEEN) ||
          removeFlags.includes(EMAIL_FLAG_SEEN)
        ) {
          window.dispatchEvent(
            new Event("korax:email-unread-changed")
          );
        }

        return true;

      } catch (err) {

        toastError(err);

        return false;
      }
    };


  const handleToggleFavorite =
    async message => {

      const favorite =
        emailHasFlag(
          message,
          EMAIL_FLAG_FAVORITE
        );


      return handleUpdateEmailFlags(
        message,
        favorite
          ? {
              removeFlags: [
                EMAIL_FLAG_FAVORITE
              ]
            }
          : {
              addFlags: [
                EMAIL_FLAG_FAVORITE
              ]
            }
      );
    };


  const handleToggleSelectedFavorite =
    async () => {

      if (
        !selectedMessageSummary
      ) {
        return;
      }


      await handleToggleFavorite(
        selectedMessageSummary
      );
    };


  const handleMarkSelectedUnread =
    async () => {

      if (
        !selectedMessageSummary
      ) {
        return;
      }


      const success =
        await handleUpdateEmailFlags(
          selectedMessageSummary,
          {
            removeFlags: [
              EMAIL_FLAG_SEEN
            ]
          }
        );


      if (success) {
        closeMessageReader();
      }
    };


  const handleOpenMessage =
    async message => {

      const companyId =
        Number(
          selectedCompanyId
        );

      const mailboxId =
        Number(
          message?.mailboxId
        );

      const uid =
        Number(
          message?.uid
        );

      if (
        !Number.isInteger(companyId) ||
        companyId <= 0 ||
        !Number.isInteger(mailboxId) ||
        mailboxId <= 0 ||
        !Number.isInteger(uid) ||
        uid <= 0
      ) {
        return;
      }

      const wasUnread =
        mailFolder === "inbox" &&
        Boolean(
          message?.unseen
        );

      setSelectedMessageSummary({
        ...message,

        _folder:
          EMAIL_FOLDER_PATHS[mailFolder] || "INBOX"
      });

      setSelectedMessage(null);

      setReplyMode(false);
      setReplyText("");
      setReplyHtml("");
      setReplyConfirming(false);
      setReplySending(false);
      setReplySent(false);

      setMessageLoading(true);

      try {

        const {
          data
        } =
          await api.get(
            "/email-beta/message",
            {
              params: {
                companyId,
                mailboxId,
                uid,

                folder:
                  EMAIL_FOLDER_PATHS[mailFolder] || "INBOX"
              }
            }
          );

        setSelectedMessage(
          data
        );

        /*
         * Confirma a flag de leitura no provedor.
         * Nao depende do efeito colateral da busca /text.
         */
        if (wasUnread) {
          await handleUpdateEmailFlags(
            {
              ...message,
              mailboxId,
              uid,
              _folder:
                EMAIL_FOLDER_PATHS[mailFolder] || "INBOX"
            },
            {
              addFlags: [EMAIL_FLAG_SEEN]
            }
          );
        }

      } catch (err) {

        setSelectedMessageSummary(
          null
        );

        toastError(err);

      } finally {

        setMessageLoading(
          false
        );
      }
    };


  const closeMessageReader = () => {

    if (messageLoading) {
      return;
    }

    setSelectedMessage(null);
    setSelectedMessageSummary(null);

    setReplyMode(false);
    setReplyText("");
    setReplyHtml("");
    setReplyConfirming(false);
    setReplySending(false);
    setReplySent(false);
  };


  const handleDownloadAttachment =
    async attachment => {

      const attachmentId =
        String(
          attachment?.id ||
          attachment?.attachmentId ||
          ""
        ).trim();

      const companyId =
        Number(
          selectedCompanyId
        );

      const mailboxId =
        Number(
          selectedMessage
            ?.mailbox
            ?.id ||
          selectedMessageSummary
            ?.mailboxId
        );

      const uid =
        Number(
          selectedMessage
            ?.uid ||
          selectedMessageSummary
            ?.uid
        );

      const folder =
        selectedMessageSummary
          ?._folder ||
        "INBOX";

      if (
        !attachmentId ||
        !Number.isInteger(
          companyId
        ) ||
        companyId <= 0 ||
        !Number.isInteger(
          mailboxId
        ) ||
        mailboxId <= 0 ||
        !Number.isInteger(
          uid
        ) ||
        uid <= 0
      ) {
        return;
      }

      setDownloadingAttachmentId(
        attachmentId
      );

      try {

        const response =
          await api.get(
            "/email-beta/attachment",
            {
              params: {
                companyId,
                mailboxId,
                uid,
                folder,
                attachmentId
              },

              responseType:
                "blob"
            }
          );

        const blob =
          response.data instanceof Blob
            ? response.data
            : new Blob(
                [
                  response.data
                ],
                {
                  type:
                    attachment
                      ?.contentType ||
                    "application/octet-stream"
                }
              );

        const objectUrl =
          window.URL
            .createObjectURL(
              blob
            );

        const link =
          document.createElement(
            "a"
          );

        link.href =
          objectUrl;

        link.download =
          String(
            attachment
              ?.filename ||
            attachment
              ?.name ||
            "anexo"
          );

        document.body
          .appendChild(
            link
          );

        link.click();

        link.remove();

        window.URL
          .revokeObjectURL(
            objectUrl
          );

      } catch (err) {

        toastError(err);

      } finally {

        setDownloadingAttachmentId(
          ""
        );
      }
    };


  const handleStartReply = () => {

    if (
      !selectedMessage ||
      messageLoading
    ) {
      return;
    }

    setReplyText("");
    setReplyHtml("");
    setReplyConfirming(false);
    setReplySent(false);
    setReplyMode(true);
  };


  const handleCancelReply =
    async () => {

      if (replySending) {
        return;
      }


      await saveReplyDraftNow();


      setReplyMode(false);

      setReplyText("");

      setReplyHtml("");

      setReplyInlineImages([]);
        setReplyAttachments([]);
        setReplyAttachmentError("");

      setReplyConfirming(false);

      setReplyDraftKey("");

      setReplyDraftReady(false);

      setReplyDraftStatus("");
    };


  const handlePrepareReply = () => {

    const clean =
      String(
        replyText || ""
      ).trim();

    if (!clean) {
      return;
    }

    if (replyAttachmentLoading || replyAttachmentError) return;
    setReplyConfirming(true);
  };



  const handleReplyAttachmentFiles = async event => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";

    if (!files.length || replySending || replyAttachmentLoading) return;

    const currentSize = replyAttachments.reduce(
      (total, item) => total + Number(item.size || 0), 0
    );

    const newSize = files.reduce(
      (total, item) => total + Number(item.size || 0), 0
    );

    if (currentSize + newSize > 15 * 1024 * 1024) {
      setReplyAttachmentError("Os anexos ultrapassam o limite de 15 MB.");
      return;
    }

    setReplyAttachmentLoading(true);
    setReplyAttachmentError("");

    try {
      const converted = await Promise.all(
        files.map(async file => ({
          filename: file.name,
          content: await fileToBase64(file),
          contentType: file.type || "application/octet-stream",
          size: file.size
        }))
      );

      setReplyAttachments(current => [...current, ...converted]);
      setReplyConfirming(false);
    } catch (_) {
      setReplyAttachmentError("Não foi possível carregar um dos anexos.");
    } finally {
      setReplyAttachmentLoading(false);
    }
  };

  const handleRemoveReplyAttachment = index => {
    setReplyAttachments(current =>
      current.filter((_, itemIndex) => itemIndex !== index)
    );
    setReplyAttachmentError("");
    setReplyConfirming(false);
  };

  const handleSendReply =
    async () => {

      const companyId =
        Number(
          selectedCompanyId
        );

      const mailboxId =
        Number(
          selectedMessage
            ?.mailbox
            ?.id ||
          selectedMessageSummary
            ?.mailboxId
        );

      const uid =
        Number(
          selectedMessage
            ?.uid ||
          selectedMessageSummary
            ?.uid
        );

      const cleanText =
        String(
          replyText || ""
        ).trim();

      if (
        !Number.isInteger(
          companyId
        ) ||
        companyId <= 0 ||
        !Number.isInteger(
          mailboxId
        ) ||
        mailboxId <= 0 ||
        !Number.isInteger(
          uid
        ) ||
        uid <= 0 ||
        !cleanText
      ) {
        return;
      }

      if (replyAttachmentLoading || replyAttachmentError) return;
      setReplySending(true);

      try {

        await api.post(
          "/email-beta/reply",
          {
            companyId,

            mailboxId,

            uid,

            text:
              cleanText,

            html:
              replyHtml,

            cc:
              [],

            bcc:
              [],

            attachments:
              [
                ...replyAttachments.map(file => ({
                  filename: file.filename,
                  content: file.content,
                  contentType: file.contentType
                })),
                ...replyInlineImages
                  .filter(
                  image =>
                    String(
                      replyHtml ||
                      ""
                    ).includes(
                      `cid:${image.cid}`
                    )
                )
                .map(
                  image => ({
                    filename:
                      image.filename,

                    content:
                      image.content,

                    contentType:
                      image.contentType,

                    cid:
                      image.cid
                  })
                )
              ]
          }
        );

        await deleteEmailDraft(
          replyDraftKey
        );

        setReplyDraftKey("");

        setReplyDraftReady(false);

        setReplyDraftStatus("");

        setReplyMode(false);
        setReplyConfirming(false);
        setReplyText("");
        setReplyInlineImages([]);
        setReplyAttachments([]);
        setReplyAttachmentError("");
        setReplyHtml("");
        setReplySent(true);

        /*
         * A Hostinger marca a mensagem
         * original como respondida.
         * Mantemos a UI aberta para o
         * usuário confirmar o resultado.
         */
        setSelectedMessage(
          current => {

            if (!current) {
              return current;
            }

            return {
              ...current,

              message: {
                ...current.message,

                flags:
                  Array.from(
                    new Set([
                      ...safeArray(
                        current
                          ?.message
                          ?.flags
                      ),
                      "\\Answered"
                    ])
                  )
              }
            };
          }
        );

      } catch (err) {

        toastError(err);

      } finally {

        setReplySending(false);
      }
    };



  const buildEmailDraftKey =
    (
      type,
      mailboxId = "",
      uid = ""
    ) => {

      const userId =
        Number(
          user?.id ||
          0
        );

      const companyId =
        Number(
          selectedCompanyId ||
          user?.companyId ||
          0
        );


      if (
        !userId ||
        !companyId
      ) {
        return "";
      }


      return [
        "korax-email",
        type,
        userId,
        companyId,
        mailboxId || "none",
        uid || "none"
      ].join(":");
    };


  const composeHasDraftContent =
    () =>
      Boolean(
        String(
          composeTo ||
          ""
        ).trim() ||
        String(
          composeCc ||
          ""
        ).trim() ||
        String(
          composeBcc ||
          ""
        ).trim() ||
        String(
          composeSubject ||
          ""
        ).trim() ||
        String(
          composeText ||
          ""
        ).trim() ||
        String(
          composeHtml ||
          ""
        ).trim() ||
        composeAttachments.length ||
        composeInlineImages.length
      );


  const replyHasDraftContent =
    () =>
      Boolean(
        String(
          replyText ||
          ""
        ).trim() ||
        String(
          replyHtml ||
          ""
        ).trim() ||
        replyInlineImages.length ||
        replyAttachments.length
      );


  const saveComposeDraftNow =
    async () => {

      if (
        !composeDraftKey ||
        !composeDraftReady
      ) {
        return;
      }


      try {

        if (
          !composeHasDraftContent()
        ) {

          await deleteEmailDraft(
            composeDraftKey
          );

          setComposeDraftStatus(
            ""
          );

          return;
        }


        setComposeDraftStatus(
          "Salvando..."
        );


        await saveEmailDraft({
          key:
            composeDraftKey,

          type:
            composeMode,

          mailboxId:
            composeMailboxId,

          to:
            composeTo,

          cc:
            composeCc,

          bcc:
            composeBcc,

          subject:
            composeSubject,

          text:
            composeText,

          html:
            composeHtml,

          attachments:
            composeAttachments,

          inlineImages:
            composeInlineImages,

          updatedAt:
            Date.now()
        });


        setComposeDraftStatus(
          "Rascunho salvo"
        );

      } catch (_) {

        setComposeDraftStatus(
          "Não foi possível salvar o rascunho"
        );
      }
    };


  const saveReplyDraftNow =
    async () => {

      if (
        !replyDraftKey ||
        !replyDraftReady
      ) {
        return;
      }


      try {

        if (
          !replyHasDraftContent()
        ) {

          await deleteEmailDraft(
            replyDraftKey
          );

          setReplyDraftStatus(
            ""
          );

          return;
        }


        setReplyDraftStatus(
          "Salvando..."
        );


        await saveEmailDraft({
          key:
            replyDraftKey,

          type:
            "reply",

          text:
            replyText,

          html:
            replyHtml,

          inlineImages:
            replyInlineImages,
          attachments:
            replyAttachments,

          updatedAt:
            Date.now()
        });


        setReplyDraftStatus(
          "Rascunho salvo"
        );

      } catch (_) {

        setReplyDraftStatus(
          "Não foi possível salvar o rascunho"
        );
      }
    };


  useEffect(
    () => {

      if (
        !composeOpen ||
        !composeDraftKey ||
        composeDraftReady
      ) {
        return;
      }


      let active =
        true;


      const restore =
        async () => {

          try {

            const draft =
              await getEmailDraft(
                composeDraftKey
              );


            if (!active) {
              return;
            }


            if (draft) {

              setComposeMode(
                draft.type ||
                composeMode
              );

              setComposeMailboxId(
                String(
                  draft.mailboxId ||
                  composeMailboxId ||
                  ""
                )
              );

              setComposeTo(
                draft.to ||
                ""
              );

              setComposeCc(
                draft.cc ||
                ""
              );

              setComposeBcc(
                draft.bcc ||
                ""
              );

              setComposeSubject(
                draft.subject ||
                ""
              );

              setComposeText(
                draft.text ||
                ""
              );

              setComposeHtml(
                draft.html ||
                ""
              );

              setComposeAttachments(
                safeArray(
                  draft.attachments
                )
              );

              setComposeInlineImages(
                safeArray(
                  draft.inlineImages
                )
              );

              setComposeDraftStatus(
                "Rascunho restaurado"
              );
            }


            setComposeDraftReady(
              true
            );

          } catch (_) {

            if (active) {

              setComposeDraftReady(
                true
              );

              setComposeDraftStatus(
                "Rascunho automático indisponível"
              );
            }
          }
        };


      restore();


      return () => {
        active = false;
      };

      // eslint-disable-next-line
    },
    [
      composeOpen,
      composeDraftKey,
      composeDraftReady
    ]
  );


  useEffect(
    () => {

      if (
        !composeOpen ||
        !composeDraftKey ||
        !composeDraftReady ||
        composeSending
      ) {
        return;
      }


      setComposeDraftStatus(
        "Salvando..."
      );


      const timer =
        window.setTimeout(
          () => {
            saveComposeDraftNow();
          },
          800
        );


      return () =>
        window.clearTimeout(
          timer
        );

      // eslint-disable-next-line
    },
    [
      composeOpen,
      composeDraftKey,
      composeDraftReady,
      composeSending,
      composeMode,
      composeMailboxId,
      composeTo,
      composeCc,
      composeBcc,
      composeSubject,
      composeText,
      composeHtml,
      composeAttachments,
      composeInlineImages
    ]
  );


  useEffect(
    () => {

      if (!replyMode) {
        return;
      }


      const mailboxId =
        Number(
          selectedMessage
            ?.mailbox
            ?.id ||
          selectedMessageSummary
            ?.mailboxId ||
          0
        );


      const uid =
        Number(
          selectedMessage
            ?.uid ||
          selectedMessageSummary
            ?.uid ||
          0
        );


      if (
        !mailboxId ||
        !uid
      ) {
        return;
      }


      const key =
        buildEmailDraftKey(
          "reply",
          mailboxId,
          uid
        );


      if (
        key &&
        key !==
          replyDraftKey
      ) {

        setReplyDraftKey(
          key
        );

        setReplyDraftReady(
          false
        );

        setReplyDraftStatus(
          ""
        );
      }

      // eslint-disable-next-line
    },
    [
      replyMode,
      selectedMessage
        ?.mailbox
        ?.id,
      selectedMessage
        ?.uid,
      selectedMessageSummary
        ?.mailboxId,
      selectedMessageSummary
        ?.uid
    ]
  );


  useEffect(
    () => {

      if (
        !replyMode ||
        !replyDraftKey ||
        replyDraftReady
      ) {
        return;
      }


      let active =
        true;


      const restore =
        async () => {

          try {

            const draft =
              await getEmailDraft(
                replyDraftKey
              );


            if (!active) {
              return;
            }


            if (draft) {

              setReplyText(
                draft.text ||
                ""
              );

              setReplyHtml(
                draft.html ||
                ""
              );

              setReplyInlineImages(
                safeArray(
                  draft.inlineImages
                )
              );

              setReplyAttachments(
                safeArray(draft.attachments)
              );

              setReplyDraftStatus(
                "Rascunho restaurado"
              );
            }


            setReplyDraftReady(
              true
            );

          } catch (_) {

            if (active) {

              setReplyDraftReady(
                true
              );

              setReplyDraftStatus(
                "Rascunho automático indisponível"
              );
            }
          }
        };


      restore();


      return () => {
        active = false;
      };

    },
    [
      replyMode,
      replyDraftKey,
      replyDraftReady
    ]
  );


  useEffect(
    () => {

      if (
        !replyMode ||
        !replyDraftKey ||
        !replyDraftReady ||
        replySending
      ) {
        return;
      }


      setReplyDraftStatus(
        "Salvando..."
      );


      const timer =
        window.setTimeout(
          () => {
            saveReplyDraftNow();
          },
          800
        );


      return () =>
        window.clearTimeout(
          timer
        );

      // eslint-disable-next-line
    },
    [
      replyMode,
      replyDraftKey,
      replyDraftReady,
      replySending,
      replyText,
      replyHtml,
      replyInlineImages,
      replyAttachments
    ]
  );


  const handleDiscardComposeDraft =
    async () => {

      try {

        await deleteEmailDraft(
          composeDraftKey
        );

      } catch (_) {
        // O reset local ainda deve funcionar.
      }


      setComposeOpen(
        false
      );

      resetComposer();
    };


  const handleDiscardReplyDraft =
    async () => {

      try {

        await deleteEmailDraft(
          replyDraftKey
        );

      } catch (_) {
        // O reset local ainda deve funcionar.
      }


      setReplyMode(
        false
      );

      setReplyText(
        ""
      );

      setReplyHtml(
        ""
      );

      setReplyInlineImages(
        []
      );
      setReplyAttachments([]);
      setReplyAttachmentError("");

      setReplyConfirming(
        false
      );

      setReplyDraftKey(
        ""
      );

      setReplyDraftReady(
        false
      );

      setReplyDraftStatus(
        ""
      );
    };


  const resetComposer = () => {

    setComposeMode("new");
    setComposeInlineImages([]);
    setComposeMailboxId("");
    setComposeTo("");
    setComposeCc("");
    setComposeBcc("");
    setComposeSubject("");
    setComposeText("");
    setComposeHtml("");
    setComposeAttachments([]);
    setComposeConfirming(false);
    setComposeSending(false);
    setComposeSuccess(null);
    setComposeError("");
    setComposeDraftKey("");
    setComposeDraftReady(false);
    setComposeDraftStatus("");
  };


  useEffect(() => {
    if (!composeOpen || !composeMailboxId || !selectedCompanyId) {
      setRecipientSuggestions([]);
      return;
    }

    let active = true;

    const loadRecipients = async () => {
      try {
        const { data } = await api.get("/email-beta/sent", {
          params: {
            companyId: Number(selectedCompanyId),
            limit: 50
          }
        });

        if (!active) return;

        const addresses = new Set();

        safeArray(data?.messages)
          .filter(row =>
            String(row.mailboxId) === String(composeMailboxId)
          )
          .forEach(row => {
            const extract = value => {
              if (Array.isArray(value)) {
                value.forEach(extract);
              } else if (typeof value === "string") {
                const match = value.match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i);
                if (match) addresses.add(match[0].toLowerCase());
              } else if (value && typeof value === "object") {
                extract(value.address || value.email || value.value);
              }
            };

            extract(row.to);
          });

        setRecipientSuggestions([...addresses].sort());
      } catch (_) {
        if (active) setRecipientSuggestions([]);
      }
    };

    loadRecipients();

    return () => { active = false; };
  }, [composeOpen, composeMailboxId, selectedCompanyId]);

  const handleOpenNewEmail = () => {

    const preferred =
      mailboxFilter !== "all"
        ? mailboxes.find(
            mailbox =>
              mailbox.email ===
              mailboxFilter
          )
        : mailboxes[0];

    resetComposer();

    setComposeDraftKey(
      buildEmailDraftKey(
        "new"
      )
    );

    setComposeDraftReady(
      false
    );

    setComposeDraftStatus(
      ""
    );

    setComposeMode(
      "new"
    );

    setComposeMailboxId(
      preferred?.id
        ? String(
            preferred.id
          )
        : ""
    );

    setComposeOpen(
      true
    );
  };


  const handleOpenForward = () => {

    if (
      !selectedMessage
    ) {
      return;
    }

    resetComposer();

    setComposeDraftKey(
      buildEmailDraftKey(
        "forward",

        Number(
          selectedMessage
            ?.mailbox
            ?.id ||
          selectedMessageSummary
            ?.mailboxId ||
          0
        ),

        Number(
          selectedMessage
            ?.uid ||
          selectedMessageSummary
            ?.uid ||
          0
        )
      )
    );

    setComposeDraftReady(
      false
    );

    setComposeDraftStatus(
      ""
    );

    const message =
      selectedMessage
        ?.message ||
      {};

    const originalText =
      String(
        selectedMessage
          ?.text
          ?.text ||
        ""
      ).trim();

    const originalHtml =
      String(
        selectedMessage
          ?.text
          ?.html ||
        ""
      ).trim();

    const originalFrom =
      formatAddressList([
        message?.from
      ]);

    const originalTo =
      formatAddressList(
        message?.to
      );

    const originalDate =
      formatFullDate(
        message?.date
      );

    const originalSubject =
      String(
        message?.subject ||
        "(sem assunto)"
      ).trim();

    const forwardedBody = [
      "",
      "",
      "---------- Mensagem encaminhada ----------",
      `De: ${originalFrom || "—"}`,
      `Data: ${originalDate || "—"}`,
      `Assunto: ${originalSubject}`,
      `Para: ${originalTo || "—"}`,
      "",
      originalText
    ].join("\n");

    setComposeMode(
      "forward"
    );

    setComposeMailboxId(
      String(
        selectedMessage
          ?.mailbox
          ?.id ||
        selectedMessageSummary
          ?.mailboxId ||
        ""
      )
    );

    setComposeSubject(
      /^(enc|fwd):/i.test(
        originalSubject
      )
        ? originalSubject
        : `Enc: ${originalSubject}`
    );

    setComposeText(
      forwardedBody
    );

    // Preserva o corpo HTML quando o original possui formatacao.
    if (originalHtml) {
      const escapeHtml = value =>
        String(value || "")
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;")
          .replace(/"/g, "&quot;");

      const forwardedHeader = [
        "---------- Mensagem encaminhada ----------",
        `De: ${originalFrom || "—"}`,
        `Data: ${originalDate || "—"}`,
        `Assunto: ${originalSubject}`,
        `Para: ${originalTo || "—"}`
      ].map(escapeHtml).join("<br>");

      const doc = new DOMParser().parseFromString(
        originalHtml,
        "text/html"
      );

      // Permite somente elementos HTML apropriados para e-mail.
      const allowedTags = new Set([
        "A", "B", "BR", "BLOCKQUOTE", "CAPTION", "CENTER",
        "CODE", "DIV", "EM", "FONT", "H1", "H2", "H3",
        "H4", "H5", "H6", "HR", "I", "IMG", "LI",
        "OL", "P", "PRE", "S", "SMALL", "SPAN",
        "STRONG", "SUB", "SUP", "TABLE", "TBODY",
        "TD", "TH", "THEAD", "TR", "U", "UL"
      ]);

      const allowedAttributes = new Set([
        "alt", "align", "border", "cellpadding", "cellspacing",
        "colspan", "color", "height", "rowspan", "title",
        "valign", "width"
      ]);

      const safeUrl = (value, image = false) => {
        const url = String(value || "").trim();

        if (!url || /[\u0000-\u001f\u007f]/.test(url)) {
          return false;
        }

        return image
          ? /^(https?:\/\/|cid:)/i.test(url)
          : /^(https?:\/\/|mailto:|tel:|#)/i.test(url);
      };

      Array.from(doc.body.querySelectorAll("*"))
        .reverse()
        .forEach(node => {
          if (!allowedTags.has(node.tagName)) {
            if (node.tagName === "SCRIPT" ||
                node.tagName === "STYLE" ||
                node.tagName === "IFRAME" ||
                node.tagName === "OBJECT" ||
                node.tagName === "SVG" ||
                node.tagName === "MATH" ||
                node.tagName === "FORM" ||
                node.tagName === "TEMPLATE") {
              node.remove();
            } else {
              node.replaceWith(...Array.from(node.childNodes));
            }
            return;
          }

          Array.from(node.attributes).forEach(attr => {
            const name = attr.name.toLowerCase();
            const value = attr.value;

            if (name === "href" && node.tagName === "A" &&
                safeUrl(value)) return;

            if (name === "src" && node.tagName === "IMG" &&
                safeUrl(value, true)) return;

            if (!allowedAttributes.has(name)) {
              node.removeAttribute(attr.name);
            }
          });
        });

      setComposeHtml(
        `<div><br><br>${forwardedHeader}<br><br></div>` +
        doc.body.innerHTML
      );
    }

    setComposeOpen(
      true
    );
  };


  const closeComposer =
    async () => {

      if (
        composeSending
      ) {
        return;
      }


      await saveComposeDraftNow();


      setComposeOpen(
        false
      );


      resetComposer();
    };


  const handleAttachmentFiles =
    async event => {

      const files =
        Array.from(
          event.target.files ||
          []
        );

      event.target.value =
        "";

      if (!files.length) {
        return;
      }

      setComposeError("");

      const currentSize =
        composeAttachments.reduce(
          (
            total,
            attachment
          ) =>
            total +
            Number(
              attachment.size ||
              0
            ),
          0
        );

      const newSize =
        files.reduce(
          (
            total,
            file
          ) =>
            total +
            Number(
              file.size ||
              0
            ),
          0
        );

      if (
        currentSize +
        newSize >
        15 * 1024 * 1024
      ) {

        setComposeError(
          "Os anexos ultrapassam o limite de 15 MB."
        );

        return;
      }

      try {

        const converted =
          await Promise.all(
            files.map(
              async file => ({
                filename:
                  file.name,

                content:
                  await fileToBase64(
                    file
                  ),

                contentType:
                  file.type ||
                  "application/octet-stream",

                size:
                  file.size
              })
            )
          );

        setComposeAttachments(
          current => [
            ...current,
            ...converted
          ]
        );

        setComposeConfirming(
          false
        );

      } catch (_) {

        setComposeError(
          "Não foi possível carregar um dos anexos."
        );
      }
    };


  const handleRemoveAttachment =
    index => {

      setComposeAttachments(
        current =>
          current.filter(
            (
              _,
              itemIndex
            ) =>
              itemIndex !==
              index
          )
      );

      setComposeConfirming(
        false
      );
    };


  const handlePrepareCompose = () => {

    const recipients =
      parseEmailList(
        composeTo
      );

    if (
      !composeMailboxId
    ) {

      setComposeError(
        "Selecione a caixa que enviará o e-mail."
      );

      return;
    }

    if (
      recipients.length ===
      0
    ) {

      setComposeError(
        "Informe pelo menos um destinatário."
      );

      return;
    }

    if (
      !String(
        composeSubject ||
        ""
      ).trim()
    ) {

      setComposeError(
        "Informe o assunto."
      );

      return;
    }

    if (
      !String(
        composeText ||
        ""
      ).trim()
    ) {

      setComposeError(
        "Escreva a mensagem."
      );

      return;
    }

    setComposeError("");

    setComposeConfirming(
      true
    );
  };


  const handleSendComposedEmail =
    async () => {

      const companyId =
        Number(
          selectedCompanyId
        );

      const mailboxId =
        Number(
          composeMailboxId
        );

      if (
        !Number.isInteger(
          companyId
        ) ||
        companyId <= 0 ||
        !Number.isInteger(
          mailboxId
        ) ||
        mailboxId <= 0
      ) {
        return;
      }

      setComposeSending(
        true
      );

      setComposeError("");

      try {

        const payload = {
          companyId,

          mailboxId,

          mode:
            composeMode,

          to:
            parseEmailList(
              composeTo
            ),

          cc:
            parseEmailList(
              composeCc
            ),

          bcc:
            parseEmailList(
              composeBcc
            ),

          subject:
            String(
              composeSubject ||
              ""
            ).trim(),

          text:
            String(
              composeText ||
              ""
            ),

          html:
              composeHtml,

          attachments: [
            ...composeAttachments.map(
              attachment => ({
                filename:
                  attachment.filename,

                content:
                  attachment.content,

                contentType:
                  attachment.contentType
              })
            ),

            ...composeInlineImages
              .filter(
                image =>
                  String(
                    composeHtml ||
                    ""
                  ).includes(
                    `cid:${image.cid}`
                  )
              )
              .map(
                image => ({
                  filename:
                    image.filename,

                  content:
                    image.content,

                  contentType:
                    image.contentType,

                  cid:
                    image.cid
                })
              )
          ]
        };

        if (
          composeMode ===
          "forward"
        ) {

          payload.uid =
            Number(
              selectedMessage
                ?.uid ||
              selectedMessageSummary
                ?.uid
            );

          payload.sourceFolder =
            selectedMessageSummary
              ?._folder ||
            "INBOX";
        }

        const {
          data
        } =
          await api.post(
            "/email-beta/send",
            payload
          );

        await deleteEmailDraft(
          composeDraftKey
        );

        setComposeDraftKey("");

        setComposeDraftReady(false);

        setComposeDraftStatus("");

        setComposeSuccess(
          data
        );

        setComposeConfirming(
          false
        );

        /*
         * Se foi encaminhamento,
         * atualiza visualmente a flag.
         */
        if (
          composeMode ===
          "forward"
        ) {

          setSelectedMessage(
            current => {

              if (!current) {
                return current;
              }

              return {
                ...current,

                message: {
                  ...current.message,

                  flags:
                    Array.from(
                      new Set([
                        ...safeArray(
                          current
                            ?.message
                            ?.flags
                        ),
                        "$forwarded"
                      ])
                    )
                }
              };
            }
          );
        }

      } catch (err) {

        toastError(err);

        setComposeError(
          "Não foi possível enviar. Confira os dados e tente novamente."
        );

      } finally {

        setComposeSending(
          false
        );
      }
    };


  const connected =
    Boolean(
      providerStatus?.connected
    );


  const mailboxes =
    safeArray(
      providerStatus?.mailboxes
    );


  const messages =
    safeArray(
      inbox?.messages
    );


  const normalizeEmailSearch =
    value =>
      String(
        value ?? ""
      )
        .normalize("NFD")
        .replace(
          /[\u0300-\u036f]/g,
          ""
        )
        .toLowerCase();


  const normalizedSearch =
    normalizeEmailSearch(
      search
    ).trim();


  const filteredMessages =
    messages.filter(
      message => {

        /*
         * Caixa selecionada
         */
        if (
          mailboxFilter !==
            "all" &&
          String(
            message
              ?.mailboxAddress ||
            ""
          ) !==
            String(
              mailboxFilter ||
              ""
            )
        ) {

          return false;
        }


        /*
         * Não lidos
         */
        if (
          unreadOnly &&
          !message?.unseen
        ) {

          return false;
        }


        /*
         * Com anexo
         */
        if (
          attachmentOnly
        ) {

          const attachmentCount =
            Math.max(
              Number(
                message
                  ?.attachmentCount ||
                0
              ),

              safeArray(
                message
                  ?.attachments
              ).length
            );


          if (
            attachmentCount <= 0
          ) {

            return false;
          }
        }


        /*
         * Período
         */
        if (
          dateFilter !==
            "all"
        ) {

          const messageDate =
            new Date(
              message?.date
            );


          if (
            Number.isNaN(
              messageDate.getTime()
            )
          ) {

            return false;
          }


          const startDate =
            new Date();


          startDate.setHours(
            0,
            0,
            0,
            0
          );


          if (
            dateFilter ===
              "7"
          ) {

            startDate.setDate(
              startDate.getDate() -
                6
            );

          } else if (
            dateFilter ===
              "30"
          ) {

            startDate.setDate(
              startDate.getDate() -
                29
            );
          }


          if (
            messageDate <
              startDate
          ) {

            return false;
          }
        }


        /*
         * Busca pelos metadados
         * disponíveis na listagem.
         */
        if (
          normalizedSearch
        ) {

          const haystack =
            normalizeEmailSearch(
              [
                message
                  ?.subject,

                message
                  ?.from
                  ?.name,

                message
                  ?.from
                  ?.address,

                message
                  ?.from
                  ?.email,

                JSON.stringify(
                  message?.to ||
                  ""
                ),

                JSON.stringify(
                  message?.cc ||
                  ""
                ),

                JSON.stringify(
                  message?.bcc ||
                  ""
                ),

                message
                  ?.mailboxAddress,

                message
                  ?.messageId,

                message
                  ?.inReplyTo
              ].join(" ")
            );


          if (
            !haystack.includes(
              normalizedSearch
            )
          ) {

            return false;
          }
        }


        return true;
      }
    );


  return (
    <>
      {/* KORAX EMAIL WORKSPACE V2 */}
      <EmailWorkspaceV2
        user={user}

        companies={companies}
        selectedCompanyId={selectedCompanyId}
        onCompanyChange={handleCompanyChange}

        connected={connected}
        mailboxes={mailboxes}

        messages={messages}
        filteredMessages={filteredMessages}

        mailFolder={mailFolder}
        setMailFolder={setMailFolder}

        mailboxFilter={mailboxFilter}
        setMailboxFilter={setMailboxFilter}

        unreadOnly={unreadOnly}
        setUnreadOnly={
          setUnreadOnly
        }

        attachmentOnly={
          attachmentOnly
        }

        setAttachmentOnly={
          setAttachmentOnly
        }

        dateFilter={
          dateFilter
        }

        setDateFilter={
          setDateFilter
        }

        search={search}
        setSearch={setSearch}

        loading={loading}
        inboxLoading={inboxLoading}

        onRefresh={refreshInbox}
        onOpenMessage={handleOpenMessage}
        onToggleFavorite={
          handleToggleFavorite
        }
        onNewEmail={handleOpenNewEmail}
        onConfigure={openProviderModal}

        accessManager={
          <Box
            display="flex"
            alignItems="center"
            style={{
              gap: 8,
              flexWrap: "wrap"
            }}
          >
            <EmailSignatureManager
              companyId={selectedCompanyId}
              mailboxes={mailboxes}
            />

            <EmailAccessManager
              user={user}
              companyId={selectedCompanyId}
            />
          </Box>
        }
      />


      {/* LEITOR DE EMAIL */}

      <Dialog
        open={
          Boolean(
            selectedMessageSummary
          )
        }
        onClose={
          messageLoading
            ? undefined
            : closeMessageReader
        }
        fullWidth
        maxWidth="md"
        PaperProps={{
          style: {
            borderRadius: 16,
            minHeight: "72vh",
            maxHeight: "90vh",
            overflow: "hidden"
          }
        }}
      >
        <DialogTitle
          disableTypography
          style={{
            padding:
              "18px 22px"
          }}
        >
          <Box
            display="flex"
            alignItems="flex-start"
            justifyContent="space-between"
            style={{
              gap: 18
            }}
          >
            <Box
              style={{
                minWidth: 0
              }}
            >
              <Typography
                style={{
                  color:
                    "#101828",
                  fontSize: 17,
                  fontWeight: 900,
                  lineHeight: 1.35
                }}
              >
                {selectedMessage
                  ?.message
                  ?.subject ||
                  selectedMessageSummary
                    ?.subject ||
                  "(sem assunto)"}
              </Typography>

              <Typography
                style={{
                  marginTop: 5,
                  color:
                    "#667085",
                  fontSize: 9.5
                }}
              >
                {selectedMessage
                  ?.mailbox
                  ?.email ||
                  selectedMessageSummary
                    ?.mailboxAddress ||
                  ""}
              </Typography>
            </Box>

            <IconButton
              size="small"
              onClick={
                closeMessageReader
              }
              disabled={
                messageLoading
              }
            >
              <CloseIcon
                style={{
                  fontSize: 20
                }}
              />
            </IconButton>
          </Box>
        </DialogTitle>

        <Divider />

        <DialogContent
          style={{
            padding: 0,
            background:
              "#FFFFFF"
          }}
        >
          {messageLoading ? (

            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              style={{
                minHeight: 480
              }}
            >
              <Box
                style={{
                  textAlign:
                    "center"
                }}
              >
                <CircularProgress
                  size={28}
                />

                <Typography
                  style={{
                    marginTop: 12,
                    color:
                      "#667085",
                    fontSize: 10
                  }}
                >
                  Carregando e-mail...
                </Typography>
              </Box>
            </Box>

          ) : selectedMessage ? (

            <Box>

              {/* CABECALHO DA MENSAGEM */}

              <Box
                style={{
                  padding:
                    "18px 22px",
                  borderBottom:
                    "1px solid #EAECF0"
                }}
              >
                <Box
                  display="flex"
                  alignItems="flex-start"
                  justifyContent="space-between"
                  style={{
                    gap: 18
                  }}
                >
                  <Box
                    display="flex"
                    alignItems="flex-start"
                    style={{
                      minWidth: 0,
                      gap: 11
                    }}
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      style={{
                        width: 38,
                        height: 38,
                        flexShrink: 0,
                        borderRadius:
                          "50%",
                        background:
                          "#EEF4FF",
                        color:
                          "#175CD3",
                        fontWeight: 900,
                        fontSize: 14
                      }}
                    >
                      {String(
                        selectedMessage
                          ?.message
                          ?.from
                          ?.name ||
                        selectedMessage
                          ?.message
                          ?.from
                          ?.address ||
                        "E"
                      )
                        .trim()
                        .charAt(0)
                        .toUpperCase()}
                    </Box>

                    <Box
                      style={{
                        minWidth: 0
                      }}
                    >
                      <Typography
                        style={{
                          color:
                            "#101828",
                          fontSize: 12,
                          fontWeight: 900
                        }}
                      >
                        {senderLabel(
                          selectedMessage
                            ?.message
                        )}
                      </Typography>

                      <Typography
                        style={{
                          marginTop: 2,
                          color:
                            "#667085",
                          fontSize: 9.5
                        }}
                      >
                        {selectedMessage
                          ?.message
                          ?.from
                          ?.address ||
                          ""}
                      </Typography>

                      <Typography
                        style={{
                          marginTop: 7,
                          color:
                            "#667085",
                          fontSize: 9,
                          lineHeight: 1.6
                        }}
                      >
                        Para:{" "}
                        {formatAddressList(
                          selectedMessage
                            ?.message
                            ?.to
                        ) ||
                          "—"}
                      </Typography>

                      {formatAddressList(
                        selectedMessage
                          ?.message
                          ?.cc
                      ) && (
                        <Typography
                          style={{
                            color:
                              "#667085",
                            fontSize: 9,
                            lineHeight: 1.6
                          }}
                        >
                          Cc:{" "}
                          {formatAddressList(
                            selectedMessage
                              ?.message
                              ?.cc
                          )}
                        </Typography>
                      )}
                    </Box>
                  </Box>


                  <Typography
                    style={{
                      flexShrink: 0,
                      color:
                        "#667085",
                      fontSize: 9.5,
                      fontWeight: 700
                    }}
                  >
                    {formatFullDate(
                      selectedMessage
                        ?.message
                        ?.date
                    )}
                  </Typography>
                </Box>
              </Box>


              {/* CORPO */}

              <Box
                style={{
                  padding:
                    "22px 26px",
                  minHeight: 330
                }}
              >
                <EmailHtmlViewer
                  html={
                    selectedMessage
                      ?.text
                      ?.html ||
                    ""
                  }
                  text={
                    selectedMessage
                      ?.text
                      ?.text ||
                    ""
                  }
                  companyId={
                    Number(
                      selectedCompanyId
                    )
                  }
                  mailboxId={
                    Number(
                      selectedMessage
                        ?.mailbox
                        ?.id ||
                      selectedMessageSummary
                        ?.mailboxId
                    )
                  }
                  uid={
                    Number(
                      selectedMessage
                        ?.uid ||
                      selectedMessageSummary
                        ?.uid
                    )
                  }
                  folder={
                    EMAIL_FOLDER_PATHS[mailFolder] || "INBOX"
                  }
                  attachments={
                    safeArray(
                      selectedMessage
                        ?.message
                        ?.attachments
                    )
                  }
                />
              </Box>


              {/* RESPOSTA */}

              {(replyMode || replySent) && (
                <>
                  <Divider />

                  <Box
                    style={{
                      padding:
                        "20px 22px",
                      background:
                        "#FAFBFC"
                    }}
                  >
                    {replySent && !replyMode && (
                      <Box
                        style={{
                          padding:
                            "12px 14px",
                          borderRadius: 10,
                          border:
                            "1px solid #ABEFC6",
                          background:
                            "#ECFDF3"
                        }}
                      >
                        <Box
                          display="flex"
                          alignItems="center"
                          style={{
                            gap: 8
                          }}
                        >
                          <CheckCircleOutlineIcon
                            style={{
                              color:
                                "#12B76A",
                              fontSize: 19
                            }}
                          />

                          <Box>
                            <Typography
                              style={{
                                color:
                                  "#027A48",
                                fontSize: 11,
                                fontWeight: 900
                              }}
                            >
                              Resposta enviada
                            </Typography>

                            <Typography
                              style={{
                                marginTop: 2,
                                color:
                                  "#475467",
                                fontSize: 9.5
                              }}
                            >
                              A mensagem foi enviada por{" "}
                              <strong>
                                {selectedMessage
                                  ?.mailbox
                                  ?.email ||
                                  ""}
                              </strong>{" "}
                              e registrada pela Hostinger.
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    )}


                    {replyMode && (
                      <>
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                          style={{
                            gap: 12,
                            marginBottom: 11
                          }}
                        >
                          <Box>
                            <Typography
                              style={{
                                color:
                                  "#101828",
                                fontSize: 12,
                                fontWeight: 900
                              }}
                            >
                              Responder
                            </Typography>

                            <Typography
                              style={{
                                marginTop: 3,
                                color:
                                  "#667085",
                                fontSize: 9
                              }}
                            >
                              De{" "}
                              <strong>
                                {selectedMessage
                                  ?.mailbox
                                  ?.email ||
                                  ""}
                              </strong>
                              {" para "}
                              <strong>
                                {selectedMessage
                                  ?.message
                                  ?.from
                                  ?.address ||
                                  ""}
                              </strong>
                            </Typography>
                          </Box>

                          <Button
                            size="small"
                            onClick={
                              handleCancelReply
                            }
                            disabled={
                              replySending
                            }
                            style={{
                              textTransform:
                                "none",
                              color:
                                "#667085",
                              fontWeight: 800
                            }}
                          >
                            Cancelar
                          </Button>
                        </Box>


                        <RichEmailEditor
                          valueHtml={
                            replyHtml
                          }
                          valueText={
                            replyText
                          }
                          inlineImages={
                            replyInlineImages
                          }
                          onInlineImagesChange={
                            setReplyInlineImages
                          }
                          onChange={({
                            html,
                            text
                          }) => {

                            setReplyHtml(
                              html
                            );

                            setReplyText(
                              text
                            );

                            setReplyConfirming(
                              false
                            );
                          }}
                          disabled={
                            replySending
                          }
                          minHeight={150}
                        />

                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                          style={{
                            minHeight: 30,
                            marginTop: 5,
                            gap: 10
                          }}
                        >
                          <Typography
                            style={{
                              color:
                                replyDraftStatus.includes(
                                  "Não foi"
                                )
                                  ? "#B42318"
                                  : "#667085",
                              fontSize: 9.5,
                              fontWeight: 600
                            }}
                          >
                            {replyDraftStatus ||
                              "Rascunho automático ativo"}
                          </Typography>

                          {replyHasDraftContent() && (
                            <Button
                              size="small"
                              onClick={
                                handleDiscardReplyDraft
                              }
                              disabled={
                                replySending
                              }
                              style={{
                                minWidth: "auto",
                                padding:
                                  "3px 7px",
                                color:
                                  "#B42318",
                                textTransform:
                                  "none",
                                fontSize: 9.5,
                                fontWeight: 700
                              }}
                            >
                              Descartar rascunho
                            </Button>
                          )}
                        </Box>




                        <Box
                          style={{
                            marginTop: 12,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 10
                          }}
                        >
                          <Button
                            component="label"
                            variant="outlined"
                            startIcon={<AttachFileIcon />}
                            disabled={replySending || replyAttachmentLoading}
                            style={{
                              height: 36,
                              borderRadius: 9,
                              textTransform: "none",
                              fontWeight: 800
                            }}
                          >
                            {replyAttachmentLoading
                              ? "Carregando..."
                              : "Anexar arquivo"}
                            <input
                              hidden
                              type="file"
                              multiple
                              onChange={handleReplyAttachmentFiles}
                            />
                          </Button>
                          <Typography
                            style={{ color: "#98A2B3", fontSize: 10 }}
                          >
                            Limite total: 15 MB
                          </Typography>
                        </Box>

                        {replyAttachmentError && (
                          <Typography
                            style={{
                              color: "#B42318",
                              fontSize: 11,
                              marginTop: 7
                            }}
                          >
                            {replyAttachmentError}
                          </Typography>
                        )}

                        {replyAttachments.length > 0 && (
                          <Box
                            style={{
                              display: "grid",
                              gap: 7,
                              marginTop: 10
                            }}
                          >
                            {replyAttachments.map((file, index) => (
                              <Box
                                key={`${file.filename}-${index}`}
                                display="flex"
                                alignItems="center"
                                justifyContent="space-between"
                                style={{
                                  padding: "9px 11px",
                                  border: "1px solid #E4E7EC",
                                  borderRadius: 9,
                                  background: "#F9FAFB",
                                  gap: 8
                                }}
                              >
                                <Box style={{ minWidth: 0 }}>
                                  <Typography
                                    style={{
                                      fontSize: 11,
                                      fontWeight: 700,
                                      overflowWrap: "anywhere"
                                    }}
                                  >
                                    {file.filename}
                                  </Typography>
                                  <Typography
                                    style={{
                                      fontSize: 10,
                                      color: "#667085"
                                    }}
                                  >
                                    {bytesLabel(file.size)}
                                  </Typography>
                                </Box>
                                <Button
                                  size="small"
                                  disabled={replySending}
                                  onClick={() =>
                                    handleRemoveReplyAttachment(index)
                                  }
                                  style={{
                                    color: "#B42318",
                                    textTransform: "none",
                                    minWidth: "auto"
                                  }}
                                >
                                  Remover
                                </Button>
                              </Box>
                            ))}
                          </Box>
                        )}

                        {!replyConfirming ? (

                          <Box
                            display="flex"
                            justifyContent="flex-end"
                            style={{
                              marginTop: 10
                            }}
                          >
                            <Button
                              variant="contained"
                              color="primary"
                              startIcon={
                                <SendIcon />
                              }
                              disabled={
                                !String(
                                  replyText ||
                                  ""
                                ).trim() ||
                                replySending
                              }
                              onClick={
                                handlePrepareReply
                              }
                              style={{
                                height: 38,
                                borderRadius: 9,
                                textTransform:
                                  "none",
                                fontWeight: 850,
                                boxShadow: "none"
                              }}
                            >
                              Enviar resposta
                            </Button>
                          </Box>

                        ) : (

                          <Box
                            style={{
                              marginTop: 12,
                              padding: 13,
                              borderRadius: 10,
                              border:
                                "1px solid #FEDF89",
                              background:
                                "#FFFAEB"
                            }}
                          >
                            <Typography
                              style={{
                                color:
                                  "#93370D",
                                fontSize: 10.5,
                                fontWeight: 850
                              }}
                            >
                              Confirmar envio?
                            </Typography>

                            <Typography
                              style={{
                                marginTop: 4,
                                color:
                                  "#667085",
                                fontSize: 9.5,
                                lineHeight: 1.5
                              }}
                            >
                              Esta resposta será enviada de{" "}
                              <strong>
                                {selectedMessage
                                  ?.mailbox
                                  ?.email ||
                                  ""}
                              </strong>{" "}
                              para o remetente original{" "}
                              <strong>
                                {selectedMessage
                                  ?.message
                                  ?.from
                                  ?.address ||
                                  ""}
                              </strong>.
                            </Typography>

                            <Box
                              display="flex"
                              justifyContent="flex-end"
                              style={{
                                gap: 8,
                                marginTop: 11
                              }}
                            >
                              <Button
                                variant="outlined"
                                disabled={
                                  replySending
                                }
                                onClick={() =>
                                  setReplyConfirming(
                                    false
                                  )
                                }
                                style={{
                                  height: 36,
                                  borderRadius: 9,
                                  textTransform:
                                    "none",
                                  fontWeight: 800
                                }}
                              >
                                Revisar
                              </Button>

                              <Button
                                variant="contained"
                                color="primary"
                                startIcon={
                                  replySending
                                    ? (
                                      <CircularProgress
                                        size={14}
                                        style={{
                                          color:
                                            "inherit"
                                        }}
                                      />
                                    )
                                    : (
                                      <SendIcon />
                                    )
                                }
                                disabled={
                                  replySending
                                }
                                onClick={
                                  handleSendReply
                                }
                                style={{
                                  minWidth: 150,
                                  height: 36,
                                  borderRadius: 9,
                                  textTransform:
                                    "none",
                                  fontWeight: 850,
                                  boxShadow: "none"
                                }}
                              >
                                {replySending
                                  ? "Enviando..."
                                  : "Confirmar envio"}
                              </Button>
                            </Box>
                          </Box>
                        )}
                      </>
                    )}
                  </Box>
                </>
              )}


              {/* ANEXOS */}

              {safeArray(
                selectedMessage
                  ?.message
                  ?.attachments
              ).length > 0 && (
                <>
                  <Divider />

                  <Box
                    style={{
                      padding:
                        "17px 22px"
                    }}
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      style={{
                        gap: 6,
                        marginBottom: 10
                      }}
                    >
                      <AttachFileIcon
                        style={{
                          color:
                            "#667085",
                          fontSize: 17
                        }}
                      />

                      <Typography
                        style={{
                          color:
                            "#344054",
                          fontSize: 10.5,
                          fontWeight: 850
                        }}
                      >
                        {safeArray(
                          selectedMessage
                            ?.message
                            ?.attachments
                        ).length}
                        {" "}
                        {safeArray(
                          selectedMessage
                            ?.message
                            ?.attachments
                        ).length === 1
                          ? "anexo"
                          : "anexos"}
                      </Typography>
                    </Box>

                    <Box
                      style={{
                        display: "grid",
                        gap: 7
                      }}
                    >
                      {safeArray(
                        selectedMessage
                          ?.message
                          ?.attachments
                      ).map(
                        (
                          attachment,
                          index
                        ) => (
                          <Paper
                            key={
                              attachment
                                ?.id ||
                              attachment
                                ?.attachmentId ||
                              index
                            }
                            elevation={0}
                            style={{
                              padding:
                                "10px 12px",
                              borderRadius: 9,
                              border:
                                "1px solid #E4E7EC",
                              background:
                                "#F9FAFB"
                            }}
                          >
                            <Box
                              display="flex"
                              alignItems="center"
                              justifyContent="space-between"
                              style={{
                                gap: 12
                              }}
                            >
                              <Box
                                style={{
                                  minWidth: 0
                                }}
                              >
                                <Typography
                                  noWrap
                                  style={{
                                    color:
                                      "#344054",
                                    fontSize: 10,
                                    fontWeight: 800
                                  }}
                                >
                                  {attachment
                                    ?.filename ||
                                    attachment
                                      ?.name ||
                                    `Anexo ${index + 1}`}
                                </Typography>

                                <Typography
                                  style={{
                                    marginTop: 2,
                                    color:
                                      "#98A2B3",
                                    fontSize: 8.5
                                  }}
                                >
                                  {[
                                    attachment
                                      ?.contentType ||
                                    attachment
                                      ?.mimeType ||
                                    "",

                                    attachment
                                      ?.sizeBytes
                                      ? bytesLabel(
                                          attachment
                                            .sizeBytes
                                        )
                                      : ""
                                  ]
                                    .filter(Boolean)
                                    .join(" • ")}
                                </Typography>
                              </Box>

                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={
                                  downloadingAttachmentId ===
                                  String(
                                    attachment
                                      ?.id ||
                                    attachment
                                      ?.attachmentId ||
                                    ""
                                  )
                                    ? (
                                      <CircularProgress
                                        size={13}
                                      />
                                    )
                                    : (
                                      <GetAppIcon />
                                    )
                                }
                                disabled={
                                  Boolean(
                                    downloadingAttachmentId
                                  )
                                }
                                onClick={() =>
                                  handleDownloadAttachment(
                                    attachment
                                  )
                                }
                                style={{
                                  flexShrink: 0,
                                  height: 33,
                                  borderRadius: 8,
                                  textTransform:
                                    "none",
                                  fontWeight: 800,
                                  fontSize: 9
                                }}
                              >
                                {downloadingAttachmentId ===
                                String(
                                  attachment
                                    ?.id ||
                                  attachment
                                    ?.attachmentId ||
                                  ""
                                )
                                  ? "Baixando..."
                                  : "Baixar"}
                              </Button>
                            </Box>
                          </Paper>
                        )
                      )}
                    </Box>
                  </Box>
                </>
              )}
            </Box>

          ) : null}
        </DialogContent>

        <Divider />

        <DialogActions
          style={{
            padding:
              "13px 20px",
            justifyContent:
              "space-between"
          }}
        >
          <Typography
            style={{
              color:
                "#98A2B3",
              fontSize: 8.5
            }}
          >
            {replySent
              ? "Resposta enviada com sucesso."
              : "Ao abrir, a mensagem é marcada como lida."}
          </Typography>

          <Box
            display="flex"
            alignItems="center"
            style={{
              gap: 6,
              flexWrap: "nowrap",
              whiteSpace: "nowrap",
              overflowX: "auto",
              flexShrink: 0
            }}
          >
            {!replyMode &&
              selectedMessage &&
              mailFolder !== "sent" && (
                <Button
                  variant="outlined"
                  onClick={
                    handleMarkSelectedUnread
                  }
                  disabled={
                    messageLoading ||
                    replySending
                  }
                  style={{
                    height: 36,
                    minWidth: "auto",
                    padding: "0 12px",
                    borderRadius: 9,
                    whiteSpace: "nowrap",
                    textTransform:
                      "none",
                    fontSize: 12.5,
                    fontWeight: 800
                  }}
                >
                  Não lido
                </Button>
              )}


            {!replyMode &&
              selectedMessage && (
                <Button
                  variant="outlined"
                  onClick={
                    handleToggleSelectedFavorite
                  }
                  disabled={
                    messageLoading ||
                    replySending
                  }
                  style={{
                    height: 36,
                    minWidth: "auto",
                    padding: "0 12px",
                    borderRadius: 9,
                    whiteSpace: "nowrap",
                    textTransform:
                      "none",
                    fontSize: 12.5,
                    fontWeight: 800,
                    color:
                      emailHasFlag(
                        selectedMessageSummary,
                        EMAIL_FLAG_FAVORITE
                      )
                        ? "#B54708"
                        : "#475467",
                    borderColor:
                      emailHasFlag(
                        selectedMessageSummary,
                        EMAIL_FLAG_FAVORITE
                      )
                        ? "#FEC84B"
                        : "#D0D5DD"
                  }}
                >
                  {emailHasFlag(
                    selectedMessageSummary,
                    EMAIL_FLAG_FAVORITE
                  )
                    ? "★ Favorito"
                    : "☆ Favoritar"}
                </Button>
              )}


            {!replyMode &&
              mailFolder === "inbox" &&
              selectedMessage && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={
                    <ReplyIcon />
                  }
                  onClick={
                    handleStartReply
                  }
                  disabled={
                    messageLoading ||
                    replySending
                  }
                  style={{
                    height: 36,
                    minWidth: "auto",
                    padding: "0 14px",
                    borderRadius: 9,
                    whiteSpace: "nowrap",
                    textTransform:
                      "none",
                    fontSize: 12.5,
                    fontWeight: 850,
                    boxShadow: "none"
                  }}
                >
                  {replySent
                    ? "Responder novamente"
                    : "Responder"}
                </Button>
              )}

            {!replyMode &&
              (
                mailFolder === "inbox" ||
                mailFolder === "sent"
              ) &&
              selectedMessage && (
                <Button
                  variant="outlined"
                  startIcon={
                    <ForwardIcon />
                  }
                  onClick={
                    handleOpenForward
                  }
                  disabled={
                    messageLoading ||
                    replySending
                  }
                  style={{
                    height: 36,
                    minWidth: "auto",
                    padding: "0 12px",
                    borderRadius: 9,
                    whiteSpace: "nowrap",
                    textTransform:
                      "none",
                    fontSize: 12.5,
                    fontWeight: 850
                  }}
                >
                  Encaminhar
                </Button>
              )}

            <Button
              onClick={
                closeMessageReader
              }
              disabled={
                messageLoading ||
                replySending
              }
              variant="outlined"
              style={{
                height: 36,
                minWidth: "auto",
                padding: "0 12px",
                borderRadius: 9,
                whiteSpace: "nowrap",
                textTransform:
                  "none",
                fontSize: 12.5,
                fontWeight: 800
              }}
            >
              Voltar
            </Button>
          </Box>
        </DialogActions>
      </Dialog>


      {/* NOVO EMAIL / ENCAMINHAR */}

      <Dialog
        open={
          composeOpen
        }
        onClose={
          composeSending
            ? undefined
            : closeComposer
        }
        fullWidth
        maxWidth="md"
        PaperProps={{
          style: {
            borderRadius: 16,
            overflow: "hidden"
          }
        }}
      >
        <DialogTitle
          disableTypography
          style={{
            padding:
              "18px 22px"
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            style={{
              gap: 14
            }}
          >
            <Box>
              <Typography
                style={{
                  color:
                    "#101828",
                  fontSize: 17,
                  fontWeight: 900
                }}
              >
                {composeMode ===
                "forward"
                  ? "Encaminhar e-mail"
                  : "Novo e-mail"}
              </Typography>

              <Typography
                style={{
                  marginTop: 3,
                  color:
                    "#667085",
                  fontSize: 9.5
                }}
              >
                {composeMode ===
                "forward"
                  ? "Envie esta mensagem para outro destinatário."
                  : "Envie uma nova mensagem usando uma das suas caixas disponíveis."}
              </Typography>
            </Box>

            <IconButton
              size="small"
              disabled={
                composeSending
              }
              onClick={
                closeComposer
              }
            >
              <CloseIcon
                style={{
                  fontSize: 20
                }}
              />
            </IconButton>
          </Box>
        </DialogTitle>

        <Divider />

        <DialogContent
          style={{
            padding: 22
          }}
        >

          {composeSuccess ? (

            <Box
              style={{
                padding:
                  "35px 20px",
                textAlign:
                  "center"
              }}
            >
              <CheckCircleOutlineIcon
                style={{
                  color:
                    "#12B76A",
                  fontSize: 43
                }}
              />

              <Typography
                style={{
                  marginTop: 10,
                  color:
                    "#101828",
                  fontSize: 17,
                  fontWeight: 900
                }}
              >
                E-mail enviado
              </Typography>

              <Typography
                style={{
                  margin:
                    "6px auto 0",
                  maxWidth: 430,
                  color:
                    "#667085",
                  fontSize: 10.5,
                  lineHeight: 1.6
                }}
              >
                A mensagem foi enviada por{" "}
                <strong>
                  {composeSuccess
                    ?.mailbox
                    ?.email ||
                    ""}
                </strong>{" "}
                e uma cópia foi registrada em Enviados.
              </Typography>
            </Box>

          ) : (
            <>

              <TextField
                select
                label="De"
                fullWidth
                variant="outlined"
                value={
                  composeMailboxId
                }
                onChange={
                  event => {

                    setComposeMailboxId(
                      event
                        .target
                        .value
                    );

                    setComposeConfirming(
                      false
                    );
                  }
                }
                disabled={
                  composeSending ||
                  composeMode ===
                    "forward"
                }
                style={{
                  marginBottom: 12
                }}
              >
                {mailboxes.map(
                  mailbox => (
                    <MenuItem
                      key={
                        mailbox.id
                      }
                      value={
                        String(
                          mailbox.id
                        )
                      }
                    >
                      {mailbox.email}
                    </MenuItem>
                  )
                )}
              </TextField>


              <Autocomplete
                freeSolo
                options={recipientSuggestions.filter(address => {
                  const part = String(composeTo || "")
                    .split(/[;,]/)
                    .pop()
                    .trim()
                    .toLowerCase();
                  return part.length >= 2 && address.includes(part);
                })}
                inputValue={composeTo}
                onInputChange={(_, value, reason) => {
                  if (reason === "input" || reason === "clear") {
                    setComposeTo(value);
                    setComposeConfirming(false);
                  }
                }}
                onChange={(_, value) => {
                  if (typeof value !== "string" || !value) return;
                  const parts = String(composeTo || "").split(/[;,]/);
                  parts.pop();
                  const prefix = parts.map(item => item.trim()).filter(Boolean);
                  setComposeTo([...prefix, value].join(", "));
                  setComposeConfirming(false);
                }}
                disabled={composeSending}
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Para"
                    placeholder="cliente@empresa.com.br"
                    fullWidth
                    variant="outlined"
                    helperText="Separe vários destinatários por vírgula."
                    style={{ marginBottom: 12 }}
                  />
                )}
              />


              <Box
                style={{
                  display: "grid",
                  gridTemplateColumns:
                    "1fr 1fr",
                  gap: 10,
                  marginBottom: 12
                }}
              >
                <TextField
                  label="CC"
                  placeholder="Opcional"
                  variant="outlined"
                  value={
                    composeCc
                  }
                  onChange={
                    event => {

                      setComposeCc(
                        event
                          .target
                          .value
                      );

                      setComposeConfirming(
                        false
                      );
                    }
                  }
                  disabled={
                    composeSending
                  }
                />

                <TextField
                  label="CCO"
                  placeholder="Opcional"
                  variant="outlined"
                  value={
                    composeBcc
                  }
                  onChange={
                    event => {

                      setComposeBcc(
                        event
                          .target
                          .value
                      );

                      setComposeConfirming(
                        false
                      );
                    }
                  }
                  disabled={
                    composeSending
                  }
                />
              </Box>


              <TextField
                label="Assunto"
                fullWidth
                variant="outlined"
                value={
                  composeSubject
                }
                onChange={
                  event => {

                    setComposeSubject(
                      event
                        .target
                        .value
                    );

                    setComposeConfirming(
                      false
                    );
                  }
                }
                disabled={
                  composeSending
                }
                style={{
                  marginBottom: 12
                }}
              />


              <RichEmailEditor
                  valueHtml={
                    composeHtml
                  }
                  valueText={
                    composeText
                  }
                  inlineImages={
                    composeInlineImages
                  }
                  onInlineImagesChange={
                    setComposeInlineImages
                  }
                  onChange={({
                    html,
                    text
                  }) => {

                    setComposeHtml(
                      html
                    );

                    setComposeText(
                      text
                    );

                    setComposeConfirming(
                      false
                    );
                  }}
                  disabled={
                    composeSending
                  }
                  minHeight={220}
                />

              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                style={{
                  minHeight: 32,
                  marginTop: 5,
                  gap: 10
                }}
              >
                <Typography
                  style={{
                    color:
                      composeDraftStatus.includes(
                        "Não foi"
                      )
                        ? "#B42318"
                        : "#667085",
                    fontSize: 9.5,
                    fontWeight: 600
                  }}
                >
                  {composeDraftStatus ||
                    "Rascunho automático ativo"}
                </Typography>

                {composeHasDraftContent() && (
                  <Button
                    size="small"
                    onClick={
                      handleDiscardComposeDraft
                    }
                    disabled={
                      composeSending
                    }
                    style={{
                      minWidth: "auto",
                      padding:
                        "3px 7px",
                      color:
                        "#B42318",
                      textTransform:
                        "none",
                      fontSize: 9.5,
                      fontWeight: 700
                    }}
                  >
                    Descartar rascunho
                  </Button>
                )}
              </Box>



              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                style={{
                  marginTop: 12,
                  gap: 10
                }}
              >
                <Button
                  component="label"
                  variant="outlined"
                  startIcon={
                    <AttachFileIcon />
                  }
                  disabled={
                    composeSending
                  }
                  style={{
                    height: 38,
                    borderRadius: 9,
                    textTransform:
                      "none",
                    fontWeight: 800
                  }}
                >
                  Anexar arquivo

                  <input
                    hidden
                    multiple
                    type="file"
                    onChange={
                      handleAttachmentFiles
                    }
                  />
                </Button>

                <Typography
                  style={{
                    color:
                      "#98A2B3",
                    fontSize: 8.5
                  }}
                >
                  Limite total: 15 MB
                </Typography>
              </Box>


              {composeAttachments
                .length > 0 && (
                <Box
                  style={{
                    marginTop: 12,
                    display: "grid",
                    gap: 7
                  }}
                >
                  {composeAttachments.map(
                    (
                      attachment,
                      index
                    ) => (
                      <Paper
                        key={
                          `${attachment.filename}-${index}`
                        }
                        elevation={0}
                        style={{
                          padding:
                            "9px 11px",
                          border:
                            "1px solid #E4E7EC",
                          borderRadius: 9,
                          background:
                            "#F9FAFB"
                        }}
                      >
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                          style={{
                            gap: 10
                          }}
                        >
                          <Box
                            display="flex"
                            alignItems="center"
                            style={{
                              minWidth: 0,
                              gap: 7
                            }}
                          >
                            <AttachFileIcon
                              style={{
                                flexShrink: 0,
                                color:
                                  "#667085",
                                fontSize: 16
                              }}
                            />

                            <Box
                              style={{
                                minWidth: 0
                              }}
                            >
                              <Typography
                                noWrap
                                style={{
                                  color:
                                    "#344054",
                                  fontSize: 9.5,
                                  fontWeight: 800
                                }}
                              >
                                {attachment.filename}
                              </Typography>

                              <Typography
                                style={{
                                  color:
                                    "#98A2B3",
                                  fontSize: 8
                                }}
                              >
                                {bytesLabel(
                                  attachment.size
                                )}
                              </Typography>
                            </Box>
                          </Box>

                          <Button
                            size="small"
                            onClick={() =>
                              handleRemoveAttachment(
                                index
                              )
                            }
                            disabled={
                              composeSending
                            }
                            style={{
                              textTransform:
                                "none",
                              color:
                                "#B42318",
                              fontSize: 9,
                              fontWeight: 800
                            }}
                          >
                            Remover
                          </Button>
                        </Box>
                      </Paper>
                    )
                  )}
                </Box>
              )}


              {composeError && (
                <Box
                  style={{
                    marginTop: 12,
                    padding:
                      "10px 12px",
                    borderRadius: 9,
                    border:
                      "1px solid #FECDCA",
                    background:
                      "#FEF3F2"
                  }}
                >
                  <Typography
                    style={{
                      color:
                        "#B42318",
                      fontSize: 9.5,
                      fontWeight: 750
                    }}
                  >
                    {composeError}
                  </Typography>
                </Box>
              )}


              {composeConfirming && (
                <Box
                  style={{
                    marginTop: 13,
                    padding: 13,
                    borderRadius: 10,
                    border:
                      "1px solid #FEDF89",
                    background:
                      "#FFFAEB"
                  }}
                >
                  <Typography
                    style={{
                      color:
                        "#93370D",
                      fontSize: 10.5,
                      fontWeight: 850
                    }}
                  >
                    Confirmar envio?
                  </Typography>

                  <Typography
                    style={{
                      marginTop: 4,
                      color:
                        "#667085",
                      fontSize: 9.5,
                      lineHeight: 1.55
                    }}
                  >
                    O e-mail será enviado para{" "}
                    <strong>
                      {parseEmailList(
                        composeTo
                      ).join(", ")}
                    </strong>
                    {composeAttachments
                      .length > 0
                      ? ` com ${composeAttachments.length} ${
                          composeAttachments.length === 1
                            ? "anexo"
                            : "anexos"
                        }.`
                      : "."}
                  </Typography>
                </Box>
              )}
            </>
          )}
        </DialogContent>

        <Divider />

        <DialogActions
          style={{
            padding:
              "14px 22px",
            justifyContent:
              "space-between"
          }}
        >
          {composeSuccess ? (
            <>
              <Box />

              <Button
                variant="contained"
                color="primary"
                onClick={
                  closeComposer
                }
                style={{
                  height: 38,
                  borderRadius: 9,
                  textTransform:
                    "none",
                  fontWeight: 850,
                  boxShadow: "none"
                }}
              >
                Concluir
              </Button>
            </>

          ) : (
            <>
              <Button
                onClick={
                  closeComposer
                }
                disabled={
                  composeSending
                }
                style={{
                  textTransform:
                    "none",
                  color:
                    "#667085",
                  fontWeight: 800
                }}
              >
                Cancelar
              </Button>

              {!composeConfirming ? (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={
                    <SendIcon />
                  }
                  onClick={
                    handlePrepareCompose
                  }
                  disabled={
                    composeSending
                  }
                  style={{
                    minWidth: 125,
                    height: 38,
                    borderRadius: 9,
                    textTransform:
                      "none",
                    fontWeight: 850,
                    boxShadow: "none"
                  }}
                >
                  Enviar
                </Button>

              ) : (
                <Box
                  display="flex"
                  style={{
                    gap: 8
                  }}
                >
                  <Button
                    variant="outlined"
                    onClick={() =>
                      setComposeConfirming(
                        false
                      )
                    }
                    disabled={
                      composeSending
                    }
                    style={{
                      height: 38,
                      borderRadius: 9,
                      textTransform:
                        "none",
                      fontWeight: 800
                    }}
                  >
                    Revisar
                  </Button>

                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={
                      composeSending
                        ? (
                          <CircularProgress
                            size={14}
                            style={{
                              color:
                                "inherit"
                            }}
                          />
                        )
                        : (
                          <SendIcon />
                        )
                    }
                    onClick={
                      handleSendComposedEmail
                    }
                    disabled={
                      composeSending
                    }
                    style={{
                      minWidth: 150,
                      height: 38,
                      borderRadius: 9,
                      textTransform:
                        "none",
                      fontWeight: 850,
                      boxShadow: "none"
                    }}
                  >
                    {composeSending
                      ? "Enviando..."
                      : "Confirmar envio"}
                  </Button>
                </Box>
              )}
            </>
          )}
        </DialogActions>
      </Dialog>


      {/* MODAL HOSTINGER */}

      <Dialog
        open={
          providerModalOpen
        }
        onClose={
          testingConnection ||
          savingConnection
            ? undefined
            : closeProviderModal
        }
        fullWidth
        maxWidth="sm"
        PaperProps={{
          style: {
            borderRadius: 16,
            overflow: "hidden"
          }
        }}
      >
        <DialogTitle
          disableTypography
          style={{
            padding:
              "20px 22px 17px"
          }}
        >
          <Box
            display="flex"
            alignItems="flex-start"
            justifyContent="space-between"
          >
            <Box
              display="flex"
              alignItems="center"
              style={{
                gap: 12
              }}
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                style={{
                  width: 42,
                  height: 42,
                  flexShrink: 0,
                  borderRadius: 12,
                  color: "#FFFFFF",
                  background:
                    "linear-gradient(135deg, #010B36, #0057FF)"
                }}
              >
                <MailOutlineIcon
                  style={{
                    fontSize: 21
                  }}
                />
              </Box>

              <Box>
                <Typography
                  style={{
                    color:
                      "#101828",
                    fontSize: 17,
                    fontWeight: 900
                  }}
                >
                  Conectar Hostinger
                </Typography>

                <Typography
                  style={{
                    marginTop: 3,
                    color:
                      "#667085",
                    fontSize: 10.5
                  }}
                >
                  Valide o token e escolha as caixas que deseja conectar.
                </Typography>
              </Box>
            </Box>

            <IconButton
              size="small"
              disabled={
                testingConnection ||
                savingConnection
              }
              onClick={
                closeProviderModal
              }
            >
              <CloseIcon
                style={{
                  fontSize: 19
                }}
              />
            </IconButton>
          </Box>
        </DialogTitle>

        <Divider />

        <DialogContent
          style={{
            padding: 22
          }}
        >
          <TextField
            select
            label="Empresa"
            value={
              modalCompanyId
            }
            onChange={
              event => {

                setModalCompanyId(
                  event
                    .target
                    .value
                );

                setTestResult(null);

                setSelectedMailboxIds(
                  []
                );

                setSaveResult(null);
              }
            }
            variant="outlined"
            fullWidth
            disabled={
              testingConnection ||
              savingConnection ||
              Boolean(
                testResult
              )
            }
            style={{
              marginBottom: 14
            }}
          >
            <MenuItem
              value=""
              disabled
            >
              Selecione a empresa
            </MenuItem>

            {companies.map(
              company => (
                <MenuItem
                  key={
                    company.id
                  }
                  value={
                    String(
                      company.id
                    )
                  }
                >
                  {company.name}
                </MenuItem>
              )
            )}
          </TextField>


          <Box
            style={{
              padding: 14,
              marginBottom: 14,
              borderRadius: 11,
              border:
                "1px solid #D1E0FF",
              background:
                "#F5F8FF"
            }}
          >
            <Box
              display="flex"
              alignItems="flex-start"
              style={{
                gap: 9
              }}
            >
              <VpnKeyOutlinedIcon
                style={{
                  marginTop: 1,
                  color:
                    "#175CD3",
                  fontSize: 18
                }}
              />

              <Box>
                <Typography
                  style={{
                    color:
                      "#1849A9",
                    fontSize: 11,
                    fontWeight: 850
                  }}
                >
                  Token da Hostinger
                </Typography>

                <Typography
                  style={{
                    marginTop: 3,
                    color:
                      "#475467",
                    fontSize: 10,
                    lineHeight: 1.5
                  }}
                >
                  O token só será armazenado após a confirmação das caixas e será salvo de forma criptografada.
                </Typography>
              </Box>
            </Box>
          </Box>


          <TextField
            label="Token da Hostinger"
            placeholder="Cole aqui o token Agentic Mail"
            type={
              showApiKey
                ? "text"
                : "password"
            }
            value={
              apiKey
            }
            onChange={
              handleApiKeyChange
            }
            variant="outlined"
            fullWidth
            autoComplete="new-password"
            disabled={
              testingConnection ||
              savingConnection
            }
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    edge="end"
                    tabIndex={-1}
                    onClick={() =>
                      setShowApiKey(
                        current =>
                          !current
                      )
                    }
                  >
                    {showApiKey ? (
                      <VisibilityOff />
                    ) : (
                      <Visibility />
                    )}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />


          {testResult && (
            <Box
              style={{
                marginTop: 18
              }}
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                style={{
                  gap: 10,
                  marginBottom: 10
                }}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  style={{
                    gap: 7
                  }}
                >
                  <CheckCircleOutlineIcon
                    style={{
                      color:
                        "#12B76A",
                      fontSize: 19
                    }}
                  />

                  <Box>
                    <Typography
                      style={{
                        color:
                          "#027A48",
                        fontSize: 12,
                        fontWeight: 900
                      }}
                    >
                      Conexão validada
                    </Typography>

                    <Typography
                      style={{
                        marginTop: 2,
                        color:
                          "#667085",
                        fontSize: 9.5
                      }}
                    >
                      {selectedMailboxIds
                        .length}
                      {" de "}
                      {testedMailboxes
                        .length}
                      {" caixas selecionadas"}
                    </Typography>
                  </Box>
                </Box>

                <Button
                  size="small"
                  onClick={
                    handleToggleAllMailboxes
                  }
                  disabled={
                    savingConnection
                  }
                  style={{
                    textTransform:
                      "none",
                    fontSize: 9.5,
                    fontWeight: 850
                  }}
                >
                  {allMailboxesSelected
                    ? "Limpar seleção"
                    : "Selecionar todas"}
                </Button>
              </Box>


              <Box
                style={{
                  display: "grid",
                  gap: 7,
                  maxHeight: 300,
                  overflowY: "auto",
                  paddingRight: 3
                }}
              >
                {testedMailboxes.map(
                  mailbox => {

                    const id =
                      String(
                        mailbox
                          ?.resourceId ||
                        ""
                      );

                    return (
                      <Paper
                        key={
                          id ||
                          mailbox.address
                        }
                        elevation={0}
                        style={{
                          padding:
                            "9px 10px",
                          borderRadius: 9,
                          border:
                            "1px solid #E4E7EC"
                        }}
                      >
                        <Box
                          display="flex"
                          alignItems="center"
                          style={{
                            gap: 7
                          }}
                        >
                          <Checkbox
                            checked={
                              selectedMailboxIds
                                .includes(
                                  id
                                )
                            }
                            onChange={() =>
                              handleToggleMailbox(
                                id
                              )
                            }
                            color="primary"
                            size="small"
                          />

                          <Box
                            style={{
                              minWidth: 0
                            }}
                          >
                            <Typography
                              noWrap
                              style={{
                                color:
                                  "#101828",
                                fontSize: 10.5,
                                fontWeight: 850
                              }}
                            >
                              {mailbox.address}
                            </Typography>

                            <Typography
                              noWrap
                              style={{
                                marginTop: 2,
                                color:
                                  "#98A2B3",
                                fontSize: 8
                              }}
                            >
                              {id}
                            </Typography>
                          </Box>
                        </Box>
                      </Paper>
                    );
                  }
                )}
              </Box>
            </Box>
          )}


          {saveResult && (
            <Box
              style={{
                marginTop: 16,
                padding: 14,
                borderRadius: 11,
                border:
                  "1px solid #ABEFC6",
                background:
                  "#ECFDF3"
              }}
            >
              <Box
                display="flex"
                alignItems="flex-start"
                style={{
                  gap: 9
                }}
              >
                <CheckCircleOutlineIcon
                  style={{
                    color:
                      "#12B76A",
                    fontSize: 20
                  }}
                />

                <Box>
                  <Typography
                    style={{
                      color:
                        "#027A48",
                      fontSize: 12,
                      fontWeight: 900
                    }}
                  >
                    Hostinger conectada
                  </Typography>

                  <Typography
                    style={{
                      marginTop: 3,
                      color:
                        "#475467",
                      fontSize: 10
                    }}
                  >
                    {saveResult
                      ?.mailboxCount ||
                      0}
                    {" caixas conectadas à "}
                    {saveResult
                      ?.company
                      ?.name ||
                      "empresa"}
                    .
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>


        <Divider />

        <DialogActions
          style={{
            padding:
              "15px 22px",
            justifyContent:
              "space-between"
          }}
        >
          <Button
            onClick={
              closeProviderModal
            }
            disabled={
              testingConnection ||
              savingConnection
            }
            style={{
              textTransform:
                "none",
              color:
                "#475467",
              fontWeight: 750
            }}
          >
            {saveResult
              ? "Fechar"
              : "Cancelar"}
          </Button>


          {!testResult &&
            !saveResult && (
              <Button
                variant="contained"
                color="primary"
                disabled={
                  testingConnection ||
                  !modalCompanyId ||
                  !String(
                    apiKey || ""
                  ).trim()
                }
                onClick={
                  handleTestConnection
                }
                style={{
                  minWidth: 138,
                  height: 38,
                  borderRadius: 9,
                  textTransform:
                    "none",
                  fontWeight: 850,
                  boxShadow: "none"
                }}
              >
                {testingConnection ? (
                  <>
                    <CircularProgress
                      size={15}
                      style={{
                        marginRight: 8,
                        color: "inherit"
                      }}
                    />
                    Testando...
                  </>
                ) : (
                  "Testar conexão"
                )}
              </Button>
            )}


          {testResult &&
            !saveResult && (
              <Box
                display="flex"
                style={{
                  gap: 8
                }}
              >
                <Button
                  variant="outlined"
                  disabled={
                    savingConnection
                  }
                  onClick={
                    handleTestConnection
                  }
                  style={{
                    height: 38,
                    borderRadius: 9,
                    textTransform:
                      "none",
                    fontWeight: 800
                  }}
                >
                  Testar novamente
                </Button>

                <Button
                  variant="contained"
                  color="primary"
                  disabled={
                    savingConnection ||
                    selectedMailboxIds
                      .length === 0
                  }
                  onClick={
                    handleSaveConnection
                  }
                  style={{
                    minWidth: 150,
                    height: 38,
                    borderRadius: 9,
                    textTransform:
                      "none",
                    fontWeight: 850,
                    boxShadow: "none"
                  }}
                >
                  {savingConnection ? (
                    <>
                      <CircularProgress
                        size={15}
                        style={{
                          marginRight: 8,
                          color: "inherit"
                        }}
                      />
                      Conectando...
                    </>
                  ) : (
                    `Conectar ${
                      selectedMailboxIds.length
                    } ${
                      selectedMailboxIds.length ===
                      1
                        ? "caixa"
                        : "caixas"
                    }`
                  )}
                </Button>
              </Box>
            )}


          {saveResult && (
            <Button
              variant="contained"
              color="primary"
              onClick={
                closeProviderModal
              }
              style={{
                minWidth: 110,
                height: 38,
                borderRadius: 9,
                textTransform:
                  "none",
                fontWeight: 850,
                boxShadow: "none"
              }}
            >
              Concluir
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};


export default EmailBeta;
